import { z } from 'zod';

import { RESERVED_NAME_PREFIX, listQuerySchema, sortDirSchema } from './common.js';
import {
  actionStatusSchema,
  automationModeSchema,
  runStatusSchema,
  type ActionStatus,
  type AdPlatform,
  type AutomationMode,
  type MetricLevel,
  type RunStatus,
} from './enums.js';
import { metricKeySchema, type MetricKey } from './analytics.js';
/**
 * Faz 7 — `platform_op` eylemi için YALNIZ kimlik biçimi ve platform öneki.
 *
 * 🔴 Kataloğun KENDİSİ (`PLATFORM_OPS`, 1000+ Zod şeması) buraya GİRMİYOR: bu
 * dosya kök barrel'dan panelin her sayfasına gidiyor. `types.ts` yalnız zod ve
 * bir tip importu taşıyor — hafif. Op kimliğinin katalogda olup olmadığı ve
 * parametrelerin şemadan geçip geçmediği SUNUCUDA, `validateWorkflow`da
 * soruluyor (`apps/api/src/automation/platform-op-action.ts`).
 */
import { OP_ID_PATTERN, PLATFORM_OP_PREFIX } from './platform-ops/types.js';
/**
 * 🔴 YALNIZ TİP — `import type` ve öyle kalmalı.
 *
 * `automation-graph.ts` BU dosyadan import ediyor (düğüm `data` şemaları
 * buradaki şemaların kendisi). Ters yönde bir DEĞER importu döngüsel bağımlılık
 * olurdu ve teorik değil ölümcül: `z.discriminatedUnion(...)` henüz
 * tanımlanmamış bir sabite bakar, modül yüklenirken patlar (gerekçenin uzunu
 * `workflowUpsertSchema.graph` alanının başında).
 *
 * `import type` derlemede TAMAMEN siliniyor — çalışma zamanında bu satırdan
 * geriye hiçbir şey kalmıyor, yani döngü de oluşmuyor.
 */
import type { WorkflowGraph } from './automation-graph.js';

/**
 * AUTOMATION — kural motoru. PARA HAREKET ETTİREN KOD.
 *
 * Tasarım §4.2. §12'ye göre en yoğun test edilecek yer burası: kural
 * değerlendirmesi, guardrail kırpması, idempotency ve bütçe aritmetiği
 * unit testle kapatılır. Buradaki bir hata kullanıcının reklam bütçesini
 * yakar ve geri alınamaz.
 *
 * ---------------------------------------------------------------------------
 * Akış
 * ---------------------------------------------------------------------------
 *   scheduler → kapsam çözümü → ANALYTICS SERVİSİNDEN güncel metrikler
 *   → koşul değerlendirmesi → guardrail → (öner ise onay kuyruğu / autopilot
 *   ise doğrudan) → adapter uygular → WorkflowActionLog
 *
 * Motor KENDİ SQL'İNİ YAZMAZ; `AnalyticsQueryService`i çağırır. Kullanıcının
 * ekranda gördüğü sayı ile kuralın okuduğu sayı farklı olursa "bu kural neden
 * tetiklendi" sorusu cevapsız kalır.
 */

export const WORKFLOW_SCHEMA_VERSION = 1;

// ═══════════════════════════════════════════════════════════════════════════
// Tetikleyici
// ═══════════════════════════════════════════════════════════════════════════

/// KE1 — zamanlama. Saatler workspace ZAMAN DİLİMİNDE yorumlanır.
export const scheduleTriggerSchema = z.object({
  kind: z.literal('schedule'),
  frequency: z.enum(['hourly', 'daily', 'weekly']),
  /// `daily`/`weekly` için `HH:mm`.
  atTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Saat HH:mm biçiminde olmalı')
    .optional(),
  /// `weekly` için 1 = Pazartesi … 7 = Pazar (ISO).
  dayOfWeek: z.number().int().min(1).max(7).optional(),
});

/// KE2 — metrik koşulu tetikleyicisi ("CPC > $2, son 3 gün").
export const metricTriggerSchema = z.object({
  kind: z.literal('metric'),
  metric: metricKeySchema,
  operator: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq']),
  value: z.number(),
  /// Kaç günlük pencerede değerlendirilecek.
  windowDays: z.number().int().min(1).max(90).default(3),
  level: z.enum(['CAMPAIGN', 'ADSET', 'AD']).default('CAMPAIGN'),
  /// Metrik tetikleyicili kurallar varsayılan olarak saatte bir yoklanır.
  checkEveryMinutes: z.number().int().min(15).max(1440).default(60),
});

/**
 * KE2b — OLAY TETİKLEYİCİSİ (Faz 2 sözleşmesi, motor bağlantısı Faz 3).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden zamanlamadan ayrı bir tetikleyici tipi
 * ═══════════════════════════════════════════════════════════════════════════
 * "Yeni lead geldiğinde Slack'e yaz" kuralı zamanlamayla YAZILAMIYOR. En sık
 * yoklama 15 dakikada bir (`checkEveryMinutes` alt sınırı) ve bir lead'e 15
 * dakika sonra dönmek, lead'i kaybetmek demek. Üstelik yoklama "bu lead yeni
 * mi" sorusunu cevaplamak için kuralın kendi durumunu tutmasını gerektirirdi
 * — kural motorunda durum yok ve olmamalı.
 *
 * ---------------------------------------------------------------------------
 * 🔴 OLAY LİSTESİ KAPALI — serbest metin DEĞİL
 * ---------------------------------------------------------------------------
 * Serbest bir olay adı, yayınlayan taraf adı değiştirdiğinde kuralı SESSİZCE
 * ölü bırakırdı. Kapalı liste, yayıncı ile abone arasındaki sözleşmeyi
 * derleme zamanında tutuyor.
 *
 * `anomaly.detected`   → harcama/CPA sapması; bugün ajan tespit ediyor
 * `creative.fatigued`  → frekans eşiği aşıldı (bkz. `frequency` metriği)
 *
 * 24 Eyl 2026 — `lead.created` kaldırıldı: lead senkronu ve Lead'ler ekranı
 * ("Analiz" grubu) kullanıcı kararıyla silindi, olayın yayıncısı kalmadı.
 *
 * Faz 7b (30 Eyl 2026) — dört olay, dördünün de GERÇEK yayıncısı var:
 * `ad.disapproved`     → worker `operator/operator-watch.service.ts` (`DISAPPROVED` reklam)
 * `lead.received`      → aynı servis (lead kampanyasının dönüşüm sayısı arttı)
 * `campaign.launched`  → aynı servis (denetim defteri: kampanya yayına girdi)
 * `ad.comment.received`→ API `comments/comment-watch.service.ts` (reklama yeni yorum)
 * Gözcü operatör olayını ve kural olayını AYNI tespitten yayınlıyor (çift
 * tarama yok); worker → API yolu `QUEUE.AUTOMATION_EVENT`.
 *
 * ---------------------------------------------------------------------------
 * Koşullar olayda da çalışır ama ANLAMI DEĞİŞİR
 * ---------------------------------------------------------------------------
 * Zamanlanmış kuralda koşul kapsamdaki HER varlık için ayrı ayrı bakılıyor.
 * Olayda kapsam olayın HEDEF nesnesine iniyor (lead'in kampanyası, yorulan
 * kreatifin reklamı — `WORKFLOW_EVENT_TARGET_LEVEL`) ve koşul o varlık için
 * bir SÜZGEÇ: "yeni lead geldi AMA kampanyanın CPA'sı zaten iyiyse haber verme".
 */
export const WORKFLOW_EVENTS = [
  'anomaly.detected',
  'creative.fatigued',
  /// Faz 7b — reklam platform incelemesinden reddedildi (hedef: REKLAM).
  'ad.disapproved',
  /// Faz 7b — lead amaçlı kampanyaya yeni potansiyel müşteri geldi (hedef: KAMPANYA).
  /// Lead'in KENDİSİ (ad, e-posta) olay yükünde YOK — sayı var.
  'lead.received',
  /// Faz 7b — kampanya yayına alındı (hedef: KAMPANYA).
  'campaign.launched',
  /**
   * Faz 7b — reklama yeni yorum geldi (hedef: REKLAM; Google/YouTube'da
   * reklam hesabı — yerel reklam satırı video kimliği taşımıyor). Yükte
   * yorum METNİ ve yazarı YOK: kimlikler + sayı. Metin gerekiyorsa kural
   * süzgeci ya da operatör `<p>.comment.list` ile okuyor.
   */
  'ad.comment.received',
] as const;
export type WorkflowEvent = (typeof WORKFLOW_EVENTS)[number];
export const workflowEventSchema = z.enum(WORKFLOW_EVENTS);

/**
 * Olay koşusunda kapsamın İNDİĞİ hedefin seviyesi (Faz 7b).
 *
 * Olay yolu eskiden kuralın TÜM kapsamını koşturuyordu: bir reklamın
 * yorgunluğu, kapsamdaki kırk kampanyanın hepsini değerlendiriyordu ve
 * "bu kampanya için Slack'e yaz" kırk mesaj demekti. Artık olayın hedefi
 * kuralın kapsam seviyesine çevriliyor (reklam → reklamın kampanyası ya da
 * kampanya → kampanyanın reklamları) ve yalnız o nesneler değerlendiriliyor.
 *
 * `null` = hedefsiz olay (elle `POST /automations/events` ile yayınlanan
 * anomali): kapsamın tamamı — bugünkü davranış. Yayıncı yükte
 * `targetLevel` verirse (Google yorumu → `ACCOUNT`) bu tablo ezilir.
 */
export type WorkflowEventTargetLevel = 'ACCOUNT' | 'CAMPAIGN' | 'ADSET' | 'AD';
export const WORKFLOW_EVENT_TARGET_LEVEL: Record<WorkflowEvent, WorkflowEventTargetLevel | null> = {
  'anomaly.detected': null,
  'creative.fatigued': 'AD',
  'ad.disapproved': 'AD',
  'lead.received': 'CAMPAIGN',
  'campaign.launched': 'CAMPAIGN',
  'ad.comment.received': 'AD',
};

/// Olay süzgecinin tanıdığı reklam platformları (`OPENAI` reklam platformu değil).
export const WORKFLOW_EVENT_FILTER_PLATFORMS = ['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'TWITTER'] as const;

/// Yorum anahtar kelime süzgecinin tavanı — koşu anında her kelime her yorumda aranıyor.
export const WORKFLOW_EVENT_KEYWORDS_MAX = 30;

/**
 * Olay tetikleyicisinin İSTEĞE BAĞLI süzgeci (Faz 7b).
 *
 *   `platforms` — olayın hedefi bu platformlardan birinde değilse kural
 *                 çalışmaz (her olayda geçerli).
 *   `keywords`  — YALNIZ `ad.comment.received`: yeni yorumlardan en az biri bu
 *                 sözcüklerden birini içermeli (büyük/küçük harf ve Türkçe
 *                 İ/ı duyarsız). Metin olay yükünde YOK; değerlendirme anında
 *                 `<p>.comment.list` ile okunuyor ve karar verilince ATILIYOR —
 *                 hiçbir yere yazılmıyor (kişisel veri + güvenilmez içerik).
 *
 * Süzgeç SUSTURMADAN (`cooldownMinutes`) ÖNCE soruluyor: eşleşmeyen bir yorum
 * susturma penceresini yakmasın, ardından gelen eşleşen yorum kaçmasın.
 */
export const eventFilterSchema = z.object({
  platforms: z.array(z.enum(WORKFLOW_EVENT_FILTER_PLATFORMS)).max(5).optional(),
  keywords: z.array(z.string().trim().min(2).max(60)).max(WORKFLOW_EVENT_KEYWORDS_MAX).optional(),
});
export type WorkflowEventFilter = z.infer<typeof eventFilterSchema>;

export const eventTriggerSchema = z.object({
  kind: z.literal('event'),
  event: workflowEventSchema,
  /**
   * Aynı hedef için aynı olay bu süre içinde tekrar gelirse kural İKİNCİ KEZ
   * çalışmaz. 100 lead'lik bir gün, 100 Slack mesajı demek olmasın.
   *
   * `0` = susturma yok (her olay tetikler). Varsayılan 60 dakika: bir kampanya
   * için saatte bir bildirim, ilgiyi kaybettirmeden akışı izlenebilir tutuyor.
   */
  cooldownMinutes: z.number().int().min(0).max(1440).default(60),
  /// Faz 7b — isteğe bağlı süzgeç (platform, yorum anahtar kelimesi).
  filter: eventFilterSchema.optional(),
});

export const workflowTriggerSchema = z.discriminatedUnion('kind', [
  scheduleTriggerSchema,
  metricTriggerSchema,
  eventTriggerSchema,
]);
export type WorkflowTrigger = z.infer<typeof workflowTriggerSchema>;

/**
 * Tetikleyicinin `Workflow.nextRunAt` üzerinden zamanlanıp zamanlanmadığı.
 *
 * Olay tetikleyicili kuralın `nextRunAt`i `null` — zamanlayıcı onu ASLA
 * seçmez, kuralı olayı yayınlayan taraf uyandırır. Bu ayrımı tek bir yerde
 * tutmanın sebebi Faz 0'ın dersi: bir aksiyonun/tetikleyicinin "bağlanmamış"
 * olması, çağıran tarafın onu sessizce atlaması demekti.
 */
export function isScheduledTrigger(trigger: WorkflowTrigger): boolean {
  return trigger.kind !== 'event';
}

/**
 * Motorun BUGÜN gerçekten çalıştırdığı tetikleyici tipleri.
 *
 * `ACTION_TYPES_WITH_EXECUTOR` ile aynı fikir: sözleşme ile gerçek arasındaki
 * farkı tek bir yerde tutmak. Editör bu listeden seçtiriyor; `event` Faz 3'te
 * motora bağlanınca buraya giriyor.
 *
 * ⚠️ Şemadan çıkarılmadı, yalnız SEÇİLEBİLİR olmaktan çıkarıldı. API'den
 * gelen (ya da şablondan kurulan) olay tetikleyicili bir kural editörde
 * OKUNABİLİR olmak zorunda — okunamayan bir kural, düzenlenemeyen bir kural.
 */
export const TRIGGER_KINDS_WITH_ENGINE: readonly WorkflowTrigger['kind'][] = [
  'schedule',
  'metric',
  /// Faz 3 — `WorkflowEventsService` olayı alıp eşleşen kuralları çalıştırıyor,
  /// `cooldownMinutes` susturmasıyla birlikte.
  'event',
];

/**
 * 🔴 BUGÜN GERÇEKTEN YAYINLANAN OLAYLAR.
 *
 * `TRIGGER_KINDS_WITH_ENGINE` "motor olayı işleyebiliyor mu" sorusunu
 * cevaplıyor; bu liste "o olayı ÜRETEN bir şey var mı" sorusunu.
 *
 * İki soruyu ayırmak zorundayız çünkü Faz 3 birincisini çözdü, ikincisini
 * çözmedi. Motor bağlı olduğu hâlde kimsenin yayınlamadığı bir olay,
 * kullanıcının kurduğu ama HİÇ ÇALIŞMAYAN bir kural demek — ve bu tam olarak
 * Faz 0'daki üç canlı hatanın sınıfı.
 *
 *   `creative.fatigued` → Faz 6 (kreatif analiz, frekans eşiği)
 *   `anomaly.detected`  → ajan; bugün yalnız `POST /automations/events`
 *
 * ⚠️ Liste BOŞ ve bu bilinçli: `POST /automations/events` bir yayıncı DEĞİL,
 * elle tetikleme ucu. Bir kullanıcının düğmeye basması "bu kural kendiliğinden
 * çalışıyor" demek değil. Faz 6 ve 7 kendi olaylarını buraya taşıyacak.
 */
export const WORKFLOW_EVENTS_WITH_PUBLISHER: readonly WorkflowEvent[] = [
  // Faz 6 — `apps/api/src/creative/creative-fatigue.scanner.ts` günde bir
  // tarayıp yayınlıyor. Teşhis, analiz ekranıyla AYNI koddan (`diagnose`)
  // geliyor: kullanıcı ekranda görmediği bir reklam için bildirim almasın.
  'creative.fatigued',
  // Faz 7b — worker `operator/operator-watch.service.ts` (5 dk'lık gözcü):
  // operatör olayıyla AYNI tespit, `QUEUE.AUTOMATION_EVENT` üzerinden API'ye.
  // Gözcü yalnız operatörü açık YA DA bu olaylara bağlı açık kuralı olan
  // alanları tarıyor.
  'ad.disapproved',
  'lead.received',
  'campaign.launched',
  // Faz 7b — API `comments/comment-watch.service.ts`: tazelik zamanlayıcısının
  // ritmiyle (`LIVE_CADENCE.comments*`) yoklanan reklam yorumları. Bayrağı/izni
  // kapalı bağlantı yoklanmıyor (bir kez loglanıyor) — kural o bağlantıda
  // tetiklenmez ve bu sessiz değil: `validateWorkflow` kapsamdaki kapalı
  // platformlar için `COMMENT_WATCH_DISABLED` uyarısı basıyor.
  'ad.comment.received',
];

// ═══════════════════════════════════════════════════════════════════════════
// Koşullar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * KE3 — AND/OR grupları, iç içe.
 *
 * Derinlik 3 ve toplam 20 kural ile SINIRLI. Doküman sınır vermiyor ama
 * sınırsız iç içelik hem editörü hem değerlendiriciyi patlatır ve kimse
 * dördüncü seviyede ne yazdığını hatırlamaz.
 */
export const MAX_CONDITION_DEPTH = 3;
export const MAX_CONDITION_RULES = 20;

export const conditionRuleSchema = z.object({
  kind: z.literal('rule'),
  metric: metricKeySchema,
  operator: z.enum(['gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between']),
  value: z.number(),
  /// `between` için üst sınır.
  valueTo: z.number().optional(),
  windowDays: z.number().int().min(1).max(90).default(7),
});
export type ConditionRule = z.infer<typeof conditionRuleSchema>;

export interface ConditionGroup {
  kind: 'group';
  op: 'AND' | 'OR';
  rules: (ConditionRule | ConditionGroup)[];
}

/// Özyinelemeli şema — `z.lazy` olmadan tip çıkarımı çöker.
///
/// ⚠️ Derinlik BAĞLI DEĞİL: `max(MAX_CONDITION_RULES)` yalnız TEK bir seviyenin
/// eleman sayısını sayıyor. Toplam yaprak ve derinlik tavanları
/// `conditionShapeErrors` ile ikinci bir aşamada uygulanıyor ve bu bilinçli —
/// `POST /automations/validate` kullanıcıya ayrıştırma hatası değil OKUNUR bir
/// sorun listesi ("koşul çok derin, en fazla 3") döndürmek zorunda; şema
/// seviyesinde reddedilseydi o uç 400 ile susardı.
///
/// İkinci aşaması OLMAYAN kapılar `boundedConditionGroupSchema` kullanır.
export const conditionGroupSchema: z.ZodType<ConditionGroup> = z.lazy(() =>
  z.object({
    kind: z.literal('group'),
    op: z.enum(['AND', 'OR']),
    rules: z.array(z.union([conditionRuleSchema, conditionGroupSchema])).min(1).max(MAX_CONDITION_RULES),
  }),
);

/**
 * Ağaç sayaçları — `packages/automation-core`den BURAYA TAŞINDI.
 *
 * Sayaçlar motorun yanında doğdu ve orada doğruydular: değerlendirici onları
 * okuyordu. Grafik sözleşmesi (`automation-graph.ts`) ikinci bir okuyucu
 * getirdi ve o okuyucu `shared`ta yaşıyor — grafiğin kaydetme kapısı
 * `filter`/`branch` düğümünün koşul ağacına AYNI tavanları uygulamak zorunda.
 * Bağımlılık yönü tek taraflı (`shared` → `automation-core` diye bir import
 * YOK), yani seçenek ikiydi: ikinci bir sayaç yazmak ya da sayacı aşağı
 * taşımak. İkincisi seçildi; aynı soruya iki cevap üretmek bu deponun en
 * pahalı hata sınıfı. `automation-core` üçünü RE-EXPORT ediyor, çağıranların
 * import satırı değişmedi.
 */
export function isGroup(node: ConditionRule | ConditionGroup): node is ConditionGroup {
  return node.kind === 'group';
}

/** Ağaçtaki toplam yaprak (kural) sayısı — gruplar sayılmaz. */
export function countRules(group: ConditionGroup): number {
  let total = 0;
  for (const node of group.rules) {
    total += isGroup(node) ? countRules(node) : 1;
  }
  return total;
}

/** Kök grup 1 kabul edilir; iç içe her grup bir seviye ekler. */
export function conditionDepth(group: ConditionGroup): number {
  let deepest = 1;
  for (const node of group.rules) {
    if (isGroup(node)) deepest = Math.max(deepest, 1 + conditionDepth(node));
  }
  return deepest;
}

/**
 * 🔴 DERİNLİĞİ ŞEMADA BAĞLI KOŞUL AĞACI — ikinci aşaması olmayan kapılar için.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden ikinci bir şema
 * ═══════════════════════════════════════════════════════════════════════════
 * Grafik kaydetme kapısında (`workflowGraphSchema`) koşul ağacına bakan ikinci
 * bir aşama YOK: `graphShapeErrors` grafiğin BÜTÜNÜNE bakıyor (döngü,
 * erişilebilirlik, port), düğümün `data`sına değil. `conditionGroupSchema`
 * doğrudan kullanılsaydı 6 seviyeli ya da 60 kurallı bir ağaç grafik üzerinden
 * `Workflow.conditions` kolonuna yazılırdı ve o kural v1 editöründe BİR DAHA
 * AÇILAMAZDI — `conditionShapeErrors` onu `CONDITION_TOO_DEEP` ile reddediyor.
 * Ölçüldü: aynı ağaç grafik kapısından geçiyor, v1 kapısından geçmiyordu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `z.lazy` + `superRefine` DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * `superRefine` ancak ayrıştırma BİTTİKTEN sonra çalışıyor ve `z.lazy`
 * özyinelemesi oraya varamıyor: 1000 seviyelik bir gövde (~38 KB, yani ucuz)
 * `safeParse`i `RangeError: Maximum call stack size exceeded` ile
 * FIRLATTIRIYOR. `safeParse` sözleşme gereği fırlatmaz; bir uç şemasında bu
 * yakalanmamış 500 demek. SONLU AÇILIM hem tavanı uyguluyor hem yığına hiç
 * inmiyor — derinlik 4'teki grup üçüncü seviyede `rule` beklendiği için
 * reddediliyor.
 *
 * ⚠️ Derinlik reddinin MESAJI `CONDITION_TOO_DEEP` DEĞİL: en derin seviyede
 * yalnız yaprak beklendiği için zod "burada `kind: 'rule'` olmalıydı" diyor.
 * Mesajı düzeltmek için ikinci bir "çok derin" kolu eklemek denendi ve geri
 * alındı — o kol yalnız reddedilmek için var olduğu hâlde tip sisteminde bir
 * `ConditionGroup` gibi davranmak zorunda kalıyor (çift `as` gerekiyor) ve
 * hatalı yazılmış bir KURAL için de "çok derin" demeye başlıyordu. Okunur kod
 * v1 yolunda (`conditionShapeErrors`) ve editörde (`automation-draft.ts` →
 * `canAddGroup`) zaten var; buradaki şema bir SON KAPI.
 */
const conditionGroupOfDepth = (remaining: number): z.ZodType<ConditionGroup> => {
  /// En derin seviyede grup YOK, yalnız yaprak — açılımı bitiren şey bu.
  const child: z.ZodType<ConditionRule | ConditionGroup> =
    remaining <= 1
      ? conditionRuleSchema
      : z.union([conditionRuleSchema, conditionGroupOfDepth(remaining - 1)]);

  return z.object({
    kind: z.literal('group'),
    op: z.enum(['AND', 'OR']),
    rules: z.array(child).min(1).max(MAX_CONDITION_RULES),
  });
};

export const boundedConditionGroupSchema: z.ZodType<ConditionGroup> = conditionGroupOfDepth(
  MAX_CONDITION_DEPTH,
).refine((group) => countRules(group) <= MAX_CONDITION_RULES, {
  // Zod'un `max(20)`u tek seviyeyi sayıyor; 3 grup × 20 kural = 60 yaprak ondan
  // kaçıyordu. `conditionShapeErrors`in `CONDITION_TOO_MANY_RULES` kodu ile
  // AYNI soru, aynı sayaç.
  message: 'CONDITION_TOO_MANY_RULES',
});

// ═══════════════════════════════════════════════════════════════════════════
// Kapsam
// ═══════════════════════════════════════════════════════════════════════════

/**
 * KE4 / SB3 — kurala hangi varlıklar girer.
 *
 * `dynamic` kip önemli: "workspace'teki tüm aktif kampanyalar" seçilirse
 * yarın açılan kampanya kurala KENDİLİĞİNDEN girer. Sabit kimlik listesi
 * kullanıcıyı her yeni kampanyada kuralı düzenlemeye zorlardı ve unutulurdu.
 *
 * Kapsam HER KOŞUDA yeniden çözülür.
 */
export const workflowScopeSchema = z.object({
  mode: z.enum(['all_active', 'selected']),
  adAccountIds: z.array(z.uuid()).max(100).default([]),
  campaignIds: z.array(z.uuid()).max(500).default([]),
  adSetIds: z.array(z.uuid()).max(500).default([]),
  /**
   * Hangi seviyede aksiyon alınacak.
   *
   * `KEYWORD` Faz 5'te açıldı ve `analytics.sql.ts`'teki `ENTITY_TABLE.KEYWORD`
   * ile AYNI COMMIT'te açılmak zorundaydı: seviyeyi tek başına açmak, kapsamı
   * sessizce boş çözülen ve neden boş olduğunu söylemeyen kurallar üretirdi.
   *
   * ⚠️ `KEYWORD` seviyesi YALNIZ Google hesaplarında iş görür. Meta'da kelime
   * kavramı yok (`canManageKeywords: false`) ve o hesaplardaki hedefler
   * `PLATFORM_HAS_NO_KEYWORDS` ile ATLANIYOR — `FAILED` değil. `validateWorkflow`
   * bunu kaydetme anında UYARI olarak da söylüyor; kullanıcı kuralın neden hiç
   * tetiklenmediğini haftalarca aramasın.
   */
  level: z.enum(['CAMPAIGN', 'ADSET', 'AD', 'KEYWORD']).default('CAMPAIGN'),
  /// Bu harcamanın altındaki varlıklar kapsam dışı — veri azken karar verilmesin.
  minSpend: z.number().min(0).default(0),
});
export type WorkflowScope = z.infer<typeof workflowScopeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Aksiyonlar
// ═══════════════════════════════════════════════════════════════════════════

export const ACTION_TYPES = [
  'pause',
  'resume',
  'budget_adjust',
  'budget_redistribute',
  'notify',
  'creative_improve',
  // ── Faz 2'de sözleşmesi yazılan, uygulayıcısı sonraki fazlarda gelenler ──
  /// Faz 5 — arama terimini negatif kelime yap.
  'add_negative_keyword',
  /// Faz 5 — anahtar kelimeyi duraklat.
  'pause_keyword',
  /// Faz 5 — teklif ayarla.
  'adjust_bid',
  /// Faz 3 — Slack/Telegram'a yaz.
  'notify_external',
  /**
   * Faz 7 — katalogdaki HERHANGİ bir platform işlemi (`opId` + `params`).
   *
   * ⚠️ Aynı dize ajanın öneri satırlarında da (`__agent__` kuralı) kullanılıyor
   * ve bu BİLİNÇLİ: onay kuyruğu ikisini aynı kartla gösteriyor
   * (`afterState.draft` → `AgentPlatformOpDraft` şekli). Yürütmeleri ayrı:
   * ajan satırı `AgentProposalService`e, kural satırı `ActionExecutorService`e
   * gidiyor (`approvals.service.ts` kuralın adına bakıyor).
   */
  'platform_op',
  /// Faz 7 — "AI'a devret": arka plan operatörüne görev + tetikleyici verisi.
  'operator_handoff',
  /**
   * Faz 7b — MENTORIVO EKRAN İŞLEMİ (`tool` + `input`): sohbet ajanının yazma
   * araçlarından biri (`AGENT_APP_TOOLS`) — kural duraklatma, rakip senkronu,
   * kreatif iyileştirme, mağaza senkronu… Uygulaması ajanın yürütücüsü
   * (`AgentAppOpExecutor`, REST'in çağırdığı yazma yönteminin kendisi).
   *
   * ⚠️ `platform_op` gibi bu dize de ajanın öneri satırlarında (`__agent__`)
   * kullanılıyor (`APP_OP_PROPOSAL_TOOL`) ve BİLİNÇLİ: onay kuyruğu ikisini
   * aynı kartla (`afterState.draft`) gösteriyor. Yürütmeleri ayrı: ajan satırı
   * `AgentProposalService`e, kural satırı `ActionExecutorService`e.
   * Hangi aracın kurala bağlanabildiği: `appOpRuleVerdict` (`automation-app-op.ts`).
   */
  'app_op',
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

/**
 * 🔴 BUGÜN GERÇEKTEN UYGULANABİLEN AKSİYONLAR.
 *
 * `ACTION_TYPES` sözleşmeyi, bu liste GERÇEĞİ anlatıyor. Aradaki fark, Faz 0'da
 * üç canlı hataya sebep olan boşluğun tam olarak kendisi: bir tip tanımlı
 * olduğu için uygulanabilir sanılıyor, uygulayıcı yok, aksiyon sessizce
 * düşüyor ve kullanıcı kuralın çalıştığını sanıyor.
 *
 * Üç yer bunu okumak ZORUNDA:
 *   - kural editörü → seçtirilecek tipler (`CHANNELS_WITH_TRANSPORT` ile aynı fikir)
 *   - `validateWorkflow` → uygulayıcısı olmayan aksiyon için UYARI
 *   - motor → planlanan aksiyon `SKIPPED` + `ACTION_NOT_IMPLEMENTED` gerekçesi
 *
 * Faz 3 `notify_external`i taşıdı; Faz 5 üç kelime aksiyonunu taşıyacak.
 */
export const ACTION_TYPES_WITH_EXECUTOR: readonly ActionType[] = [
  'pause',
  'resume',
  'budget_adjust',
  'budget_redistribute',
  'notify',
  'creative_improve',
  /**
   * Faz 3 — Slack ve WhatsApp taşıyıcıları yazıldı.
   *
   * ⚠️ Bu listede olmak "taşıyıcı VAR" demek, "bu workspace BAĞLADI" demek
   * değil. Bağlanmamış bir kanala yazan kural `MESSAGING_NOT_CONNECTED` ile
   * döner ve aksiyon `FAILED` olur — sessizce düşmez. Editör kullanıcıya
   * hangi kanalların bağlı olduğunu `GET /messaging` ile göstermeli.
   */
  'notify_external',
  /**
   * Faz 5 — üçü de İKİ yolda birden bağlandı ve bu şart:
   *   • zamanlanmış kural → `apps/worker/.../google-keyword-writer.ts`
   *   • elle/onaylı       → `action-executor.service.ts`
   * Yalnız biri bağlansaydı aksiyon "bazen çalışır" olurdu — hata sınıfının
   * en kötüsü, çünkü kullanıcı hangi koşulda çalıştığını asla çıkaramaz.
   *
   * ⚠️ Bu listede olmak "uygulayıcı VAR" demek, "bu hesapta İŞ GÖRÜR" demek
   * değil. Meta'da anahtar kelime kavramı yok; oradaki hedefler
   * `SKIPPED` + `PLATFORM_HAS_NO_KEYWORDS` ile geçiliyor (`FAILED` değil —
   * başarısız olan bir şey yok). `validateWorkflow` bunu kaydetme anında
   * uyarı olarak da söylüyor.
   */
  'add_negative_keyword',
  'pause_keyword',
  'adjust_bid',
  /**
   * Faz 7 — iki yeni eylem, İKİ YOLDA birden bağlı:
   *   • planlama → `@mentorivo/automation-core` `planAction` (worker'ın
   *     zamanlanmış koşusu ve API'nin elle/olay/webhook koşusu AYNI fonksiyon)
   *   • uygulama → `action-executor.service.ts` (zamanlanmış satır
   *     `QUEUE.AUTOMATION_WRITE` ile oraya düşüyor, onaylı satır doğrudan)
   *
   * `platform_op` yürütmesi `PlatformOpsService` üzerinden (idempotency,
   * sahiplik, canlı bütçe yoklaması) ve onaysız kuralda bile `decideOp`
   * güvenlik zarfından geçiyor — hassas / geri alınamaz / yayına alma / tavan
   * dışı işlem onay kuyruğuna düşüyor.
   */
  'platform_op',
  'operator_handoff',
  /**
   * Faz 7b — İKİ YOLDA birden, `platform_op` ile aynı yer:
   *   • planlama → `automation-core` `planAction` (koşu başına tek satır)
   *   • uygulama → `action-executor.service.ts` → `AppOpActionService`
   *     (zamanlanmış satır `QUEUE.AUTOMATION_WRITE` ile, onaylı satır doğrudan)
   * Onaysız yalnız ajanın dar otomatik pilot listesi (`appOpAutopilotEligible`);
   * gerisi her koşuda onay kuyruğuna iniyor.
   */
  'app_op',
];

/**
 * 🔴 PLATFORM VARLIĞININ DURUMUNU YA DA BÜTÇESİNİ DEĞİŞTİREN AKSİYONLAR.
 *
 * Diğerlerinden ayrılmalarının sebebi tek bir gerçek: bunlar `AdAccount`
 * üzerinden bir connector çağrısı yapıyor ve o çağrının hangi platformlarda
 * çalıştığı UYGULAMA YOLUNA göre değişiyor (bkz. aşağıdaki liste).
 *
 * `notify`, `notify_external` ve `creative_improve` bu listede DEĞİL
 * ve olmamalı: hiçbiri platforma yazmıyor, dolayısıyla platform sorusu onlar
 * için hiç doğmuyor. Kelime aksiyonları da ayrı (`KEYWORD_ACTION_TYPES`) —
 * onların platform kısıtı bir yetenek değil bir KAVRAM sorusu.
 */
export const ENTITY_WRITE_ACTION_TYPES: readonly ActionType[] = [
  'pause',
  'resume',
  'budget_adjust',
  'budget_redistribute',
];

/**
 * 🔴 ZAMANLANMIŞ KOŞUDA `ENTITY_WRITE_ACTION_TYPES` GERÇEKTEN HANGİ
 * PLATFORMLARA YAZABİLİYOR.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * `ACTION_TYPES_WITH_EXECUTOR` ile aynı fikir, farklı eksen
 * ═══════════════════════════════════════════════════════════════════════════
 * O liste "bu aksiyonun uygulayıcısı var mı" diyor; bu liste "o uygulayıcı bu
 * platformda çalışıyor mu" diyor.
 *
 * 29 Eyl 2026: BEŞ platform. Faz 4'te worker'ın Meta'ya özel yazıcısı
 * (`meta-writer.ts`, `AutomationApplyService`) silindi; worker artık yalnız
 * aktarıcı (`action.processor.ts`: `AUTOMATION_ACTION` → `AUTOMATION_WRITE`)
 * ve hem onaylı (`SUGGEST`) hem zamanlanmış (`AUTOPILOT`) yol API'nin
 * `ActionExecutorService` → `PlatformActionService` → `ConnectorRegistry`
 * zincirinden geçiyor. Liste o commit'te genişlemeliydi ama `['META']` kaldı;
 * editör ve `validateWorkflow` Meta dışı her autopilot kurala YANLIŞ
 * `AUTOPILOT_PLATFORM_UNSUPPORTED` uyarısı gösteriyordu.
 *
 * Üç yer bunu okumak ZORUNDA (yeni bir platform eklenince uyarı kendiliğinden
 * çıksın diye):
 *   - kural editörü → kip + kapsam platformu uyuşmuyorsa uyarı
 *   - `validateWorkflow` → `AUTOPILOT_PLATFORM_UNSUPPORTED` UYARISI
 *   - motor → desteklenmeyen platformda `FAILED`; gerekçe log satırında
 */
export const AUTOPILOT_ENTITY_WRITE_PLATFORMS: readonly AdPlatform[] = [
  'META',
  'GOOGLE',
  'TIKTOK',
  'LINKEDIN',
  'TWITTER',
];

/**
 * Kelime seviyesinde çalışan aksiyonlar.
 *
 * ✅ Faz 5 — `workflowScopeSchema.level` artık `KEYWORD` KABUL EDİYOR ve
 * `analytics.sql.ts`'teki `ENTITY_TABLE.KEYWORD` de dolu; ikisi aynı commit'te
 * açıldı. Seviyeyi tek başına açmak, kapsamı boş çözülen ve neden boş
 * olduğunu söylemeyen kurallar üretirdi.
 *
 * ⚠️ Seviye YALNIZ Google hesaplarında iş görüyor. Meta/TikTok/LinkedIn/X'te
 * kelime kavramı yok (`canManageKeywords: false`) ve o hedefler `SKIPPED` +
 * `PLATFORM_HAS_NO_KEYWORDS` ile geçiliyor — `FAILED` değil.
 */
export const KEYWORD_ACTION_TYPES: readonly ActionType[] = [
  'add_negative_keyword',
  'pause_keyword',
  'adjust_bid',
];

const pauseAction = z.object({ type: z.literal('pause') });
const resumeAction = z.object({ type: z.literal('resume') });

/// KE6 — bütçe artır/azalt.
const budgetAdjustAction = z.object({
  type: z.literal('budget_adjust'),
  direction: z.enum(['increase', 'decrease']),
  /// `percent` → yüzde, `fixed` → hesabın para biriminde sabit tutar.
  unit: z.enum(['percent', 'fixed']),
  amount: z.number().positive(),
  /// Alt/üst sınır — kural bütçeyi bunların dışına taşıyamaz.
  minBudget: z.number().min(0).optional(),
  maxBudget: z.number().min(0).optional(),
});

/**
 * KE7 — "kazanana bütçe kaydır". §4.2 aksiyonu SAYIYOR ama algoritmayı
 * VERMİYOR. Para hareket ettiren en riskli aksiyon; tanımsız bırakılamaz.
 *
 * Sözleşme kararı:
 *   1. Kapsamdaki varlıklar `winnerMetric` ile sıralanır (varsayılan ROAS).
 *   2. `lookbackDays` penceresinde harcaması `minSpend`in altında kalanlar
 *      DEĞERLENDİRMEYE GİRMEZ — az veriyle kazanan seçmek kumar.
 *   3. Alt `loserPct` dilimindekilerden bütçenin `takePct`i alınır.
 *   4. Alınan toplam, üst `winnerPct` dilimine harcama ağırlığıyla dağıtılır.
 *   5. TOPLAM BÜTÇE SABİT KALIR — bu aksiyon harcamayı artırmaz, yeniden
 *      dağıtır. Artırmak isteyen `budget_adjust` kullanır.
 *   6. Her hedefteki değişim `Workspace.maxBudgetChangePercent` tavanına
 *      ayrıca takılır; takılan aksiyon SKIPPED olur, sessizce kırpılmaz.
 */
const budgetRedistributeAction = z.object({
  type: z.literal('budget_redistribute'),
  winnerMetric: z.enum(['roas', 'cpa', 'cpc', 'ctr', 'conversions']).default('roas'),
  lookbackDays: z.number().int().min(1).max(30).default(7),
  minSpend: z.number().min(0).default(50),
  /// Alt dilimden alınacak bütçe yüzdesi.
  takePct: z.number().min(1).max(50).default(20),
  loserPct: z.number().min(10).max(50).default(25),
  winnerPct: z.number().min(10).max(50).default(25),
});

const notifyAction = z.object({
  type: z.literal('notify'),
  /// Kimlere: rol bazlı ya da belirli üyeler.
  target: z.enum(['owners_admins', 'all_members', 'creator']).default('owners_admins'),
  channels: z.array(z.enum(['IN_APP', 'EMAIL'])).min(1).default(['IN_APP']),
  message: z.string().trim().max(500).optional(),
});

/// KE10 — kreatif iyileştirme görevi aç. Stüdyo kuyruğuna DRAFT olarak düşer;
/// kredi kullanıcı üretimi başlatana kadar HARCANMAZ.
const creativeImproveAction = z.object({
  type: z.literal('creative_improve'),
  variants: z.number().int().min(1).max(4).default(2),
});

// ─────────────── Faz 2 sözleşmesi — uygulama Faz 3 ve Faz 5 ───────────────

/**
 * Arama terimini negatif kelime yap. Wask'ın en çok kurulan kelime şablonu:
 * "dönüşüm getirmeyen ama para yakan arama terimini kapat".
 *
 * `matchType` VARSAYILANI `EXACT` ve bu güvenlik kararı. `BROAD` negatif
 * kelime, "ücretsiz" gibi tek bir sözcük yüzünden onlarca meşru aramayı
 * birden keser ve bu geri alınırken de fark edilmez — trafik düşer, sebebi
 * görünmez. Tam eşleşme yalnız o terimi kapatır.
 *
 * `scope`: negatif kelime kampanyaya mı reklam grubuna mı eklenecek.
 * Varsayılan `campaign` — aynı terimin kardeş reklam gruplarında tekrar para
 * yakmasını engelliyor.
 */
const addNegativeKeywordAction = z.object({
  type: z.literal('add_negative_keyword'),
  matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']).default('EXACT'),
  scope: z.enum(['campaign', 'ad_group']).default('campaign'),
});

/// Anahtar kelimeyi duraklat. Alan taşımıyor: hedef zaten kapsam çözümünden
/// geliyor, `pause` aksiyonunun kelime karşılığı.
const pauseKeywordAction = z.object({ type: z.literal('pause_keyword') });

/**
 * Teklif ayarla — `budget_adjust`ın kelime karşılığı ve alanları BİLEREK ona
 * ayna. İki ayrı şekil olsaydı editör iki ayrı form, guardrail iki ayrı
 * hesap isterdi.
 *
 * ⚠️ Guardrail farkı: bütçe tavanı `Workspace.maxBudgetChangePercent`.
 * Teklifin ayrı bir tavanı YOK ve şema donmuş — Faz 5 aynı tavanı teklife de
 * uyguluyor. Aynı tavanı paylaşmak, teklifi bütçeden daha serbest bırakmaktan
 * güvenli: teklif bütçeyi dolaylı olarak zaten harcatıyor.
 */
const adjustBidAction = z.object({
  type: z.literal('adjust_bid'),
  direction: z.enum(['increase', 'decrease']),
  unit: z.enum(['percent', 'fixed']),
  amount: z.number().positive(),
  minBid: z.number().min(0).optional(),
  maxBid: z.number().min(0).optional(),
});

/**
 * Slack/WhatsApp'a yaz — `notify`den AYRI bir aksiyon tipi.
 *
 * Neden `notify`nin `channels` listesini büyütmüyoruz: `notify`nin hedefi
 * KİŞİ (`owners_admins`, `creator`) ve kanal o kişiye nasıl ulaşılacağı.
 * Slack/WhatsApp'ta hedef bir ODA — kişiyle ilgisi yok, workspace'in bağladığı
 * bir kanal ya da numara. Aynı alanın iki farklı anlam taşıması, `target`ın
 * Slack'te ne demek olduğu sorusunu cevapsız bırakırdı.
 *
 * `destination` boşsa workspace'in varsayılan odası kullanılır
 * (`MessagingConnection.defaultDestination`, bağlanırken seçiliyor). Kural
 * başına oda seçimi ileri kullanım.
 *
 * ⚠️ `TELEGRAM` Faz 3'te kaldırıldı (bkz. `enums.ts` → `NotificationChannel`).
 * Kayıtlı bir kural hâlâ `TELEGRAM` taşıyorsa Zod onu REDDEDER — bu bilinçli:
 * gönderilemeyecek bir kanalı sessizce kabul etmek, Faz 0'ın üç canlı
 * hatasının sınıfı. Böyle bir satır zaten yok (tablo sayıldı, boştu).
 */
const notifyExternalAction = z.object({
  type: z.literal('notify_external'),
  channels: z.array(z.enum(['SLACK', 'WHATSAPP'])).min(1).default(['SLACK']),
  /// Kanal içindeki hedef (Slack kanal adı / WhatsApp numarası ya da grup
  /// kimliği). Boşsa workspace varsayılanı.
  destination: z.string().trim().max(120).optional(),
  message: z.string().trim().max(500).optional(),
});

// ─────────────── Faz 7 — katalog işlemi + "AI'a devret" ───────────────

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 PARAMETRE BAĞLAMA — İFADE DİLİ DEĞİL, KAPALI BİR YER TUTUCU LİSTESİ
 * ═══════════════════════════════════════════════════════════════════════════
 * Kural "koşulu tutan kampanyayı KOPYALA" diyebilmeli: işlemin `campaignRef`
 * parametresi kuralı kurarken bilinmiyor, koşu anında hedefin kimliği. Bu
 * sürümde grafik ifade dili YOK ve bilinçli (`automation-graph.ts` →
 * `GraphEdgeValue` başlığı); `{{…}}` açılımı onun yerine geçmiyor:
 *
 *   · Yalnız aşağıdaki BEŞ belirteç tanınıyor. Başka her `{{…}}` kaydetmede
 *     HATA (`PLATFORM_OP_BINDING_UNKNOWN`) — sessizce literal kalan bir yer
 *     tutucu, platforma `"{{campaign.id}}"` diye bir kimlik göndermek olurdu.
 *   · Değerler YALNIZ bizim veritabanımızdan (hedefin senkronla yazılmış
 *     satırı) geliyor; değerlendirici yok, yol gezme yok, kullanıcı girdisi
 *     çalıştırılmıyor.
 *   · Açılım dize İÇİNDE de çalışıyor (Google kaynak adları
 *     `customers/{{account.platformRef}}/campaigns/{{entity.platformRef}}`
 *     biçiminde), ama yalnız DİZE yapraklarda; anahtar adları açılmıyor.
 *
 * Bağlama yoksa işlem KOŞU BAŞINA BİR KEZ çalışıyor (`notify` gibi). Bağlama
 * varsa hedef başına (ya da yalnız `account.*` ise hesap başına) — aynı
 * `pixel.create` işlemini 40 kampanya için 40 kez çalıştırmamak için.
 */
export const PLATFORM_OP_BINDING_TOKENS = [
  /// Hedefin platform kimliği (kampanya / reklam seti / reklam / kelime) —
  /// yerel satırdaki biçimiyle (Google kaynak adı, TikTok `reklamveren~kimlik`).
  'entity.platformRef',
  /**
   * Hedefin MENTORIVO kimliği (UUID). Katalogdaki varlık referansı alanları
   * (`opEntityRef`) bunu işleyicide HESABA DARALTILMIŞ olarak platform
   * kimliğine çeviriyor (`ctx.resolveRef`) — platforma özgü kimlik biçimini
   * bilmeyen en güvenli seçenek.
   */
  'entity.id',
  /// Hedefin adı — "Kopya: {{entity.name}}" gibi.
  'entity.name',
  /// Hedefin kampanyasının platform kimliği (kampanya hedefinde kendisi).
  'entity.campaignRef',
  /// Hedefin reklam setinin platform kimliği (reklam seti hedefinde kendisi).
  'entity.adSetRef',
  /// Hedefin bağlı olduğu reklam hesabının platform kimliği (`act_…`, müşteri kimliği).
  'account.platformRef',
] as const;
export type PlatformOpBindingToken = (typeof PLATFORM_OP_BINDING_TOKENS)[number];

/// `{{ entity.platformRef }}` — iç boşluk serbest. Tanınmayan içerik de yakalanıyor
/// ki doğrulama onu REDDEDEBİLSİN (yalnız tanınanı yakalasaydık `{{foo}}` literal kalırdı).
const BINDING_SOURCE = String.raw`\{\{\s*([^{}]*?)\s*\}\}`;

/** Parametre ağacındaki her dize yaprağı — anahtar yolu ile. */
function walkStrings(
  value: unknown,
  visit: (text: string, path: string) => void,
  path = '',
  depth = 0,
): void {
  if (depth > 12) return;
  if (typeof value === 'string') {
    visit(value, path);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => walkStrings(item, visit, `${path}[${index}]`, depth + 1));
    return;
  }
  if (value && typeof value === 'object') {
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      walkStrings(item, visit, path ? `${path}.${key}` : key, depth + 1);
    }
  }
}

export interface PlatformOpBindingScan {
  /// Kullanılan tanınan belirteçler (tekil).
  tokens: PlatformOpBindingToken[];
  /// Tanınmayan `{{…}}` içerikleri — kaydetmede hata.
  unknown: string[];
  /// Belirteç taşıyan parametre yolları (`campaignRef`, `refs[0]`).
  paths: string[];
}

export function scanPlatformOpBindings(params: unknown): PlatformOpBindingScan {
  const tokens = new Set<PlatformOpBindingToken>();
  const unknown = new Set<string>();
  const paths = new Set<string>();
  walkStrings(params, (text, path) => {
    for (const match of text.matchAll(new RegExp(BINDING_SOURCE, 'g'))) {
      const name = (match[1] ?? '').trim();
      if ((PLATFORM_OP_BINDING_TOKENS as readonly string[]).includes(name)) {
        tokens.add(name as PlatformOpBindingToken);
      } else {
        unknown.add(name);
      }
      paths.add(path);
    }
  });
  return { tokens: [...tokens], unknown: [...unknown], paths: [...paths] };
}

/**
 * İşlem hangi birimde çalışır:
 *   `entity`  — `entity.*` bağlaması var → hedef başına bir satır
 *   `account` — yalnız `account.*` → reklam hesabı başına bir satır
 *   `run`     — bağlama yok → koşu başına bir satır
 */
export type PlatformOpBindingUnit = 'entity' | 'account' | 'run';

export function platformOpBindingUnit(params: unknown): PlatformOpBindingUnit {
  const { tokens } = scanPlatformOpBindings(params);
  if (tokens.some((token) => token.startsWith('entity.'))) return 'entity';
  if (tokens.length > 0) return 'account';
  return 'run';
}

/**
 * Belirteçleri değerlerle değiştirir. Değeri OLMAYAN belirteç (`entity.adSetRef`
 * kampanya hedefinde) `missing`e yazılıyor ve dize OLDUĞU GİBİ kalıyor —
 * çağıran eksik varsa işlemi ÇALIŞTIRMIYOR (`PLATFORM_OP_BINDING_UNRESOLVED`).
 *
 * Dizenin TAMAMI tek bir belirteçse sonuç yine dize (sayıya çevrilmiyor): platform
 * kimlikleri 19 haneli olabiliyor ve `Number` onları sessizce yuvarlardı.
 */
export function applyPlatformOpBindings(
  params: Record<string, unknown>,
  values: Partial<Record<PlatformOpBindingToken, string | null>>,
): { params: Record<string, unknown>; missing: PlatformOpBindingToken[] } {
  const missing = new Set<PlatformOpBindingToken>();
  const replace = (value: unknown, depth: number): unknown => {
    if (depth > 12) return value;
    if (typeof value === 'string') {
      return value.replace(new RegExp(BINDING_SOURCE, 'g'), (whole, raw: string) => {
        const name = raw.trim() as PlatformOpBindingToken;
        if (!(PLATFORM_OP_BINDING_TOKENS as readonly string[]).includes(name)) return whole;
        const resolved = values[name];
        if (typeof resolved !== 'string' || resolved.length === 0) {
          missing.add(name);
          return whole;
        }
        return resolved;
      });
    }
    if (Array.isArray(value)) return value.map((item) => replace(item, depth + 1));
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, replace(item, depth + 1)]),
      );
    }
    return value;
  };
  return { params: replace(params, 0) as Record<string, unknown>, missing: [...missing] };
}

/**
 * Op kimliğinin platformu — öneke göre (`meta.` → META, `x.` → TWITTER).
 *
 * Kataloğa bakmadan: motor SAF ve kataloğu yüklemiyor, yalnız "bu işlem bu
 * hedefin platformunda mı" sorusunu soruyor. Katalogda OLMAYAN bir kimlik
 * zaten kaydetmede reddediliyor.
 */
export function platformOfOpId(opId: string): AdPlatform | null {
  for (const [platform, prefix] of Object.entries(PLATFORM_OP_PREFIX) as [AdPlatform, string][]) {
    if (opId.startsWith(prefix)) return platform;
  }
  return null;
}

/// Parametre gövdesinin JSON tavanı — kural belgesi, dışa aktarma ve compose
/// istemine giriyor; büyük bir liste (müşteri listesi) kural gövdesinde yaşamamalı.
export const PLATFORM_OP_PARAMS_MAX_CHARS = 20_000;

/**
 * Katalog işlemi — `opId` + `params`.
 *
 * Şema yalnız ŞEKLİ tutuyor (kimlik biçimi, nesne, boyut). Kimliğin katalogda
 * olması, parametrelerin işlemin Zod şemasından geçmesi, kişisel veri taşıyan
 * işlemlerin reddi ve bağlamaların geçerliliği `validateWorkflow`da — bu dosya
 * kataloğu yüklemiyor (gerekçe dosya başındaki import notunda).
 */
const platformOpAction = z.object({
  type: z.literal('platform_op'),
  opId: z.string().trim().max(120).regex(OP_ID_PATTERN, 'PLATFORM_OP_ID_INVALID'),
  /// İşlemin GİRDİ biçimi (katalog JSON Şeması `io: 'input'`). Belirteç içerebilir.
  params: z
    .record(z.string(), z.unknown())
    .default({})
    .refine((params) => JSON.stringify(params).length <= PLATFORM_OP_PARAMS_MAX_CHARS, {
      message: 'PLATFORM_OP_PARAMS_TOO_LARGE',
    }),
});

/**
 * "AI'a devret" — kural tetiklenince arka plan operatörüne bir GÖREV açılır:
 * tetikleyicinin verisi (hangi kural, neden, hangi hedefler, metrikleri) +
 * kullanıcının yazdığı talimat. Operatör KENDİ politikasıyla karar veriyor;
 * bu eylem platforma hiçbir şey yazmıyor.
 *
 * İki hız sınırı, ikisi de LLM koşusunun maliyeti ve gürültü için:
 *   `cooldownMinutes`     — kural başına: bu pencerede en fazla BİR devir.
 *   `entityCooldownHours` — aynı hedef bu süre içinde İKİNCİ KEZ devredilmez.
 */
const operatorHandoffAction = z.object({
  type: z.literal('operator_handoff'),
  instruction: z.string().trim().min(10).max(2000),
  cooldownMinutes: z.number().int().min(15).max(7 * 24 * 60).default(60),
  entityCooldownHours: z.number().int().min(1).max(30 * 24).default(24),
});

/// Ekran işlemi girdisinin JSON tavanı — `PLATFORM_OP_PARAMS_MAX_CHARS` ile aynı gerekçe.
export const APP_OP_INPUT_MAX_CHARS = 20_000;

/// Araç adı biçimi (`automation_set_enabled`). Aracın VAR olması ve kurala
/// bağlanabilmesi SUNUCUDA (`validateWorkflow` → `analyzeAppOpAction`).
export const APP_OP_TOOL_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

/**
 * Faz 7b — Mentorivo ekran işlemi: `AGENT_APP_TOOLS`taki bir YAZMA aracı.
 *
 * Şema yalnız ŞEKLİ tutuyor. Aracın sözlükte olması, kurala bağlanabilmesi
 * (`appOpRuleVerdict`) ve girdinin aracın KENDİ Zod şemasından geçmesi
 * `validateWorkflow`da — araç şemaları API'de (`agent/app-tools/*.tools.ts`)
 * ve bu dosya panelin her sayfasına gidiyor.
 *
 * 🔴 Belirteç (`{{entity.*}}`) YOK: ekran araçları platform nesnesine değil
 * Mentorivo kaydına (kural, rakip, kreatif) dokunuyor ve kimlikleri kuralı
 * kurarken biliniyor. İşlem koşu başına BİR KEZ çalışıyor (`notify` gibi).
 */
const appOpAction = z.object({
  type: z.literal('app_op'),
  tool: z.string().trim().max(64).regex(APP_OP_TOOL_PATTERN, 'APP_OP_TOOL_INVALID'),
  input: z
    .record(z.string(), z.unknown())
    .default({})
    .refine((input) => JSON.stringify(input).length <= APP_OP_INPUT_MAX_CHARS, {
      message: 'APP_OP_INPUT_TOO_LARGE',
    }),
});

export const workflowActionSchema = z.discriminatedUnion('type', [
  pauseAction,
  resumeAction,
  budgetAdjustAction,
  budgetRedistributeAction,
  notifyAction,
  creativeImproveAction,
  addNegativeKeywordAction,
  pauseKeywordAction,
  adjustBidAction,
  notifyExternalAction,
  platformOpAction,
  operatorHandoffAction,
  appOpAction,
]);
export type WorkflowAction = z.infer<typeof workflowActionSchema>;

/// KE11 — sıralı liste. `order` sürükle-bırak için; sunucu diziyi bu alana
/// göre değil DİZİ SIRASINA göre uygular, `order` yalnız arayüz ipucu.
export const workflowActionsSchema = z.array(workflowActionSchema).min(1).max(10);

// ═══════════════════════════════════════════════════════════════════════════
// Kural CRUD
// ═══════════════════════════════════════════════════════════════════════════

export const workflowUpsertSchema = z.object({
  /**
   * 🔴 `__` ÖN EKİ YASAK (Faz 4'te bulundu).
   *
   * Kural listeleri `__agent__` adlı satırı gizliyor. Kullanıcı kuralına
   * `__agent__` adını verirse kendi kuralını kendi ekranından gizlemiş olur —
   * ve onu bir daha ne düzenleyebilir ne silebilir, çünkü `requireWorkflow`
   * o adı reddediyor. Kurtarma yolu yok, o yüzden kapı girişte kapalı.
   *
   * `__agent__` yerine tüm `__` ön eki: aynı desenin ikinci bir örneği
   * doğduğunda (bir gün doğacak) bu kısıt kendiliğinden kapsar.
   */
  name: z
    .string()
    .trim()
    .min(2)
    .max(120)
    .refine((n) => !n.startsWith(RESERVED_NAME_PREFIX), { message: 'RESERVED_NAME_PREFIX' }),
  description: z.string().trim().max(500).optional(),
  enabled: z.boolean().default(false),
  /**
   * KE14 / AY3 — MOD ÖNCELİĞİ.
   *
   * §4.2 hem workspace hem kural bazında mod tanımlıyor ama ÇAKIŞMA KURALI
   * VERMİYOR. Sözleşme kararı (güvenli yön): workspace `SUGGEST` ise
   * workspace'teki HİÇBİR kural autopilot çalışamaz — kural `AUTOPILOT` olsa
   * bile öneriye düşer. Workspace `AUTOPILOT` ise kural kendi modunu seçer.
   *
   * Tersi olsaydı workspace ayarı bir güvenlik anahtarı olmaktan çıkardı.
   */
  mode: automationModeSchema.default('SUGGEST'),
  trigger: workflowTriggerSchema,
  conditions: conditionGroupSchema,
  actions: workflowActionsSchema,
  scope: workflowScopeSchema,
  schemaVersion: z.literal(WORKFLOW_SCHEMA_VERSION).default(WORKFLOW_SCHEMA_VERSION),
  /**
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 GRAFİK (schemaVersion 2) — İSTEĞE BAĞLI, ŞEMASI BU DOSYADA DEĞİL
   * ═══════════════════════════════════════════════════════════════════════
   * Doluysa kural bir AKIŞ olarak kaydediliyor ve v1 kolonları (`trigger`,
   * `conditions`, `actions`, `scope`) grafiğin İZDÜŞÜMÜNDEN yazılıyor
   * (`projectionFromGraph`). Boşsa hiçbir şey değişmiyor — doğrusal kural
   * bugünkü gibi kaydediliyor ve motor onu `graphFromLegacy` ile açıyor.
   *
   * ---------------------------------------------------------------------------
   * ⚠️ NEDEN `workflowGraphSchema` DEĞİL, `z.unknown()`
   * ---------------------------------------------------------------------------
   * Gerçek şema `automation-graph.ts`te ve o dosya BU dosyadan import ediyor
   * (`workflowActionSchema`, `workflowScopeSchema`, tetikleyici şemaları —
   * düğüm `data` şekilleri mevcut şemaların KENDİSİ). Buradan oraya bir import
   * eklemek DÖNGÜSEL BAĞIMLILIK olurdu ve teorik değil ölümcül: `automation-
   * graph.ts` gövdesi, `automation.ts` gövdesi çalışmadan önce yürütülür ve
   * `z.discriminatedUnion('kind', [scheduleTriggerSchema, …])` henüz
   * tanımlanmamış bir sabite bakar — modül yüklenirken patlar.
   *
   * İkinci bir kısıt: `z.custom<WorkflowGraph>()` de kullanılamıyor. Bu şema
   * `z.toJSONSchema()` ile modele araç tanımı olarak veriliyor
   * (`compose.service.ts`, `agent.tools.ts`) ve zod özel tipleri JSON Schema'ya
   * çeviremeyip FIRLATIYOR — ölçüldü.
   *
   * 🔴 BU YÜZDEN DOĞRULAMA KAPIDA DEĞİL, `validateWorkflow`TA:
   * `workflow-validate.ts` alanı `workflowGraphSchema` ile ayrıştırıyor ve
   * başarısızlığı `error` seviyesinde bildiriyor. Kaydetme yolu (`create` /
   * `update`) ve `/automations/validate` ucu aynı fonksiyonu çağırdığı için
   * doğrulanmamış bir grafik veritabanına GİREMEZ. Tip `unknown` olarak
   * bırakıldı ve bilerek: `WorkflowGraph` diye etiketlemek, henüz ayrıştırılmamış
   * bir gövdeyi ayrıştırılmış gibi göstermek olurdu.
   */
  graph: z.unknown().optional(),
});
export type WorkflowUpsertInput = z.infer<typeof workflowUpsertSchema>;

export interface WorkflowRow {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  mode: AutomationMode;
  /// OT1 — okunur tetikleyici özeti; i18n anahtarı + parametre, düz metin değil.
  triggerSummary: { key: string; params: Record<string, string | number> };
  /**
   * Kural kartındaki ORTA düğüm: "3 koşul · hepsi".
   *
   * Kural listesi bir tablo değil akış kartı ızgarası: her kart kuralın üç
   * parçasını (ne zaman → hangi koşul → ne yapılacak) küçük bir diyagram
   * olarak gösteriyor. Koşul parçası olmadan diyagram eksik kalıyordu ve
   * kullanıcı "bu kural neye bakıyor" sorusunu ancak kuralı açarak
   * cevaplayabiliyordu.
   *
   * Koşulun METNİ değil SAYISI dönüyor. Ağaç özyinelemeli ve üç seviyeli bir
   * ağacı tek satıra indiren bir cümle ("EBM > 50 VEYA (ROAS < 2 VE harcama >
   * 100)") kartta okunmuyor, kırpılıyor. Sayı ile bağlaç, kartın taşıyabileceği
   * kadarını doğru taşıyor; tamamı editörde.
   *
   * `count` kök gruptaki KURAL yapraklarının toplamı — alt gruplardakiler
   * dahil, gruplar sayılmıyor.
   */
  conditionSummary: { count: number; op: 'AND' | 'OR' };
  actionCount: number;
  actionTypes: ActionType[];
  lastRunAt: string | null;
  lastRunStatus: RunStatus | null;
  nextRunAt: string | null;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * OT7 — "bağlantı koptuğu için duraklatıldı".
   *
   * `Workflow`'da `pausedReason` kolonu YOK ve şema donmuş. Bu bilgi OKUMA
   * ANINDA kapsamdaki bağlantıların durumundan TÜRETİLİR. `enabled` bayrağına
   * DOKUNULMAZ: kullanıcının açtığı kuralı sistemin kapatması, bağlantı
   * düzelince kimin geri açacağı sorusunu doğurur.
   */
  blocked: {
    reason: 'connection_lost' | 'entitlement' | 'no_scope' | null;
    /// Kopuk hesapların adları — kullanıcı hangisini düzelteceğini bilsin.
    details: string[];
  };
  /// Etkin mod (workspace ile çözülmüş hâli). `mode` kullanıcının seçtiği,
  /// bu ise gerçekte uygulanacak olan.
  effectiveMode: AutomationMode;
}

export interface WorkflowDetail extends WorkflowRow {
  trigger: WorkflowTrigger;
  conditions: ConditionGroup;
  actions: WorkflowAction[];
  scope: WorkflowScope;
  schemaVersion: number;
  /**
   * Kayıtlı AKIŞ (schemaVersion 2) — editörün tuvali bunu okuyor.
   *
   * `null` = kural doğrusal ve tuval `graphFromLegacy` ile açılıyor. Bozuk bir
   * `graph` kolonu da `null` dönüyor: açılamayan bir belge ile hiç olmayan bir
   * belge, okuyan taraf için AYNI karar (doğrusal gövdeye düş).
   *
   * ⚠️ Tip `unknown`: gerçek şekli `WorkflowGraph` ama bu arayüz
   * `automation-graph.ts`i import EDEMEZ (bağımlılık yönü tek taraflı, döngü
   * modül yüklenirken patlar — gerekçe `workflowUpsertSchema.graph`ta).
   * Okuyan taraf `workflowGraphSchema` ile daraltıyor.
   */
  graph: unknown;
  /// Kapsam çözümünün ŞU ANKİ sonucu — editör "bu kural 12 kampanyaya
  /// dokunuyor" diyebilsin.
  resolvedTargetCount: number;
}

export const workflowListQuerySchema = listQuerySchema.extend({
  enabled: z.enum(['true', 'false']).transform((v) => v === 'true').optional(),
  mode: automationModeSchema.optional(),
  lastRunStatus: runStatusSchema.optional(),
  sort: z.enum(['createdAt', 'name', 'lastRunAt']).default('createdAt'),
  dir: sortDirSchema,
});

// ═══════════════════════════════════════════════════════════════════════════
// Doğrulama ve kuru çalıştırma
// ═══════════════════════════════════════════════════════════════════════════

/// KE16 — editör bunu alan altına basar. İKİ AYRI doğrulama mantığı yazılmaz;
/// kaydetme de aynı fonksiyonu çağırır.
export interface ValidationIssue {
  level: 'error' | 'warning';
  /// JSON yolu: `actions[1].amount`, `conditions.rules[0].value`.
  path: string;
  code: string;
  /// i18n anahtarı; arayüz çevirir.
  messageKey: string;
  params?: Record<string, string | number>;
}

export interface ValidationResponse {
  issues: ValidationIssue[];
  /// `error` seviyesinde sorun yoksa true.
  valid: boolean;
}

/**
 * Faz 7 — `POST /automations/platform-op/check`: editörün TEK işlem kontrolü.
 *
 * Kural bütününü doğrulamadan (`/automations/validate`) ayrı çünkü editörün
 * sorusu daha dar ve daha sık: "bu işlem + bu parametreler geçerli mi, etkisi
 * ne, hassas mı ve bu kuralda onay isteyecek mi". Hataların KENDİSİ aynı
 * fonksiyondan geliyor (`platformOpActionIssues`) — kaydetmede görülen hata ile
 * burada görülen aynı cümle.
 */
export const platformOpCheckSchema = z.object({
  opId: z.string().trim().min(1).max(120),
  params: z.record(z.string(), z.unknown()).default({}),
  /// Kapsam seviyesi — bağlamalı işlemde seviye uyuşmazlığı uyarısı için.
  scopeLevel: z.enum(['CAMPAIGN', 'ADSET', 'AD', 'KEYWORD']).optional(),
  /// Kuralın KENDİ kipi; çalışma alanı kipiyle sunucuda birleşiyor.
  mode: automationModeSchema.default('SUGGEST'),
});
export type PlatformOpCheckInput = z.infer<typeof platformOpCheckSchema>;

export interface PlatformOpCheckResponse {
  /// Katalogda var mı.
  known: boolean;
  op: {
    id: string;
    platform: AdPlatform;
    kind: 'read' | 'write';
    level: string;
    title: { en: string; tr: string };
    reversible: boolean;
    dryRun: boolean;
    /// Bağlayıcıda işleyicisi var mı — yoksa koşuda `OP_NOT_IMPLEMENTED`.
    implemented: boolean;
  } | null;
  /// Parametrelerden çözülen harcama etkisi; parametreler geçersizse `null`.
  impact: 'NONE' | 'DECREASE' | 'INCREASE' | 'NEW_SPEND' | null;
  /// `ACCESS` · `DESTINATION` · `DATA_SHARING` · `LEGAL` · `PUBLISHING` · `FUNDS`.
  sensitivity: string | null;
  unit: PlatformOpBindingUnit;
  bindings: PlatformOpBindingToken[];
  /**
   * Bu kuralda onay isteyecek mi — ÖNİZLEME.
   *   `APPROVAL`    — her koşuda (kip Öner, hassas, geri alınamaz, yayına alma kapalı…)
   *   `CONDITIONAL` — koşu anındaki bütçe/kotaya göre (yüzde tavanı, günlük sayaç)
   *   `AUTO`        — tavanlar içinde onaysız
   * Kesin karar koşu anında `decideOp` ile veriliyor (canlı bütçe + son 24 saat).
   */
  approval: { verdict: 'AUTO' | 'APPROVAL' | 'CONDITIONAL'; reasons: string[] };
  issues: ValidationIssue[];
}

/**
 * Faz 7b — `GET /automations/app-ops`: kural editörünün "Mentorivo işlemi"
 * seçicisi. Sözlükteki HER yazma aracı geliyor — bağlanamayanlar da, sebebiyle
 * (`block`): kullanıcı "ekip daveti neden yok" sorusunun cevabını seçicide
 * görsün, aramada kaybolmuş sanmasın.
 */
export interface AppOpCatalogEntry {
  tool: string;
  domain: string;
  /// `appOpRuleVerdict` — kurala bağlanabilir mi, onay kuralı ne.
  bindable: boolean;
  block: string | null;
  /// `AUTOPILOT_OK` = otomatik pilotta onaysız OLABİLİR (girdiye bağlı: ör. yalnız
  /// kuralı DURAKLATMA); `ALWAYS` = her koşuda onay kuyruğu.
  approval: 'AUTOPILOT_OK' | 'ALWAYS';
  spendsCredits: boolean;
  /// Kartı kim onaylayabilir.
  decisionRole: 'VIEWER' | 'MEMBER' | 'ADMIN' | 'OWNER';
}

/// `GET /automations/app-ops/:tool` — girdi şeması dahil (modelin gördüğü şemanın KENDİSİ).
export interface AppOpCatalogDetail extends AppOpCatalogEntry {
  /// İngilizce araç açıklaması (modele giden metin) — editörde not olarak.
  description: string;
  inputSchema: Record<string, unknown>;
}

/// `POST /automations/app-op/check` — tek işlemin kontrolü (kaydetmeyle AYNI fonksiyon).
export const appOpCheckSchema = z.object({
  tool: z.string().trim().min(1).max(64),
  input: z.record(z.string(), z.unknown()).default({}),
  mode: automationModeSchema.default('SUGGEST'),
});
export type AppOpCheckInput = z.infer<typeof appOpCheckSchema>;

export interface AppOpCheckResponse {
  known: boolean;
  entry: AppOpCatalogEntry | null;
  /**
   * Bu kuralda onay isteyecek mi — `AUTO` yalnız etkin kip AUTOPILOT VE
   * işlem ajanın dar otomatik pilot listesinde (girdiyle birlikte) ise.
   */
  approval: { verdict: 'AUTO' | 'APPROVAL'; reasons: string[] };
  issues: ValidationIssue[];
}

/**
 * KE15 — kuru çalıştırma. "Bu kural son 7 günde kaç kez, hangi kampanyada
 * tetiklenirdi." HİÇBİR ŞEY KAYDETMEZ — `WorkflowRun` satırı bile açmaz
 * (şemada `dryRun` bayrağı yok, sahte koşu kaydı geçmişi kirletir).
 *
 * Kaydedilmemiş kural gövdesiyle de çalışır: kullanıcı kaydetmeden denemeli.
 */
export const dryRunSchema = z.object({
  /// Var olan kural üzerinde denemek için.
  workflowId: z.uuid().optional(),
  /// Ya da kaydedilmemiş taslak.
  draft: workflowUpsertSchema.optional(),
  days: z.number().int().min(1).max(30).default(7),
}).refine((v) => Boolean(v.workflowId) !== Boolean(v.draft), 'Kural kimliği ya da taslak verin');
export type DryRunInput = z.infer<typeof dryRunSchema>;

export interface DryRunHit {
  date: string;
  targetId: string;
  targetName: string;
  targetLevel: MetricLevel;
  /// Kararın dayandığı metrikler — "CPC $2.40, eşik $2.00".
  inputMetrics: Partial<Record<MetricKey, number | null>>;
  /**
   * Ne yapılırdı (okunur özet, i18n anahtarı + parametre).
   *
   * `nodeId` HANGİ KUTU olduğunu söylüyor ve satırın değil ENTRY'nin üzerinde:
   * bir hit (gün + hedef) dallanmış bir akışta birden çok düğümden aksiyon
   * toplayabiliyor ve "ROAS iyiyse artır, kötüyse duraklat" kuralında
   * kullanıcının görmesi gereken şey tam olarak hangi kolun çalıştığı.
   * Editör tuvalinde o kutuyu vurgulamak için de tek bağ bu.
   */
  actions: {
    type: ActionType;
    summaryKey: string;
    params: Record<string, string | number>;
    nodeId: string;
  }[];
  /// Guardrail'e takılır mıydı.
  wouldBeSkipped: boolean;
  skipReason: string | null;
}

export interface DryRunResponse {
  days: number;
  totalTriggers: number;
  hits: DryRunHit[];
  /// Kapsamda kaç varlık var — hiç tetiklenmediyse "kapsam boş mu" sorusu.
  scopeSize: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// AI ile kural yazma (compose)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * "Şu olunca şunu yap" — düz cümleden kural TASLAĞI.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Bu uç KURAL OLUŞTURMUYOR, taslak döndürüyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Modelin ürettiği şey doğrudan `Workflow` satırına yazılsaydı, kullanıcı bir
 * cümle yazıp para hareket ettiren bir kural kurmuş olurdu — hangi kampanyaya,
 * hangi eşikle, hangi kipte olduğunu görmeden. Uç `workflowUpsertSchema`
 * şeklinde bir taslak veriyor; taslak editörün tuvaline basılıyor ve
 * kaydetmeye `POST /automations` karar veriyor.
 *
 * Bunun ikinci bir faydası var: taslak KAYDEDİLMEDEN önce `validate` ucundan
 * geçiyor, yani modelin uydurduğu bir eşik ya da alan tavanını aşan bir bütçe
 * yüzdesi ekranda kırmızı olarak görünüyor.
 *
 * ---------------------------------------------------------------------------
 * `current` neden var
 * ---------------------------------------------------------------------------
 * İkinci istem çoğu zaman düzeltme oluyor ("aynısını haftalık yap", "eşiği
 * 40'a çek"). Mevcut taslak gönderilmezse model her seferinde sıfırdan bir
 * kural üretir ve kullanıcının elle yaptığı düzenlemeler sessizce silinir.
 */
export const workflowComposeSchema = z.object({
  prompt: z.string().trim().min(3).max(1000),
  /**
   * Ekranda duran taslak — varsa model bunu DEĞİŞTİRİR, sıfırdan yazmaz.
   *
   * 🔴 Tipi `workflowUpsertSchema` DEĞİL, serbest bir nesne. Sebep şu: ekranda
   * duran taslak çoğu zaman YARIM. Kullanıcı editörü açıyor, adı boş, ilk
   * isteminde "ROAS düşerse durdur" yazıyor. `workflowUpsertSchema` o gövdeyi
   * reddederdi (`name` en az 2 karakter) ve isteğin TAMAMI 400 dönerdi — hem
   * de en sık kullanılacak akışta.
   *
   * Gevşeklik güvenli çünkü bu değer hiçbir zaman yanıt olmuyor: yalnızca
   * sistem istemine metin olarak giriyor ve model her durumda TAM bir kural
   * emit ediyor. Dönen taslak `workflowUpsertSchema` ile ayrıştırılıyor.
   *
   * Boyut servis tarafında kırpılıyor (`COMPOSE_CURRENT_MAX_CHARS`); şema
   * derinliğe bakmıyor, bakması da gerekmiyor.
   */
  current: z.record(z.string(), z.unknown()).optional(),
});
export type WorkflowComposeInput = z.infer<typeof workflowComposeSchema>;

export interface WorkflowComposeResponse {
  /// Tuvale basılacak taslak. `enabled` HER ZAMAN false — bkz. compose.service.
  draft: WorkflowUpsertInput;
  /**
   * 🔴 TUVALE BASILAN ŞEY BU — `draft` DEĞİL.
   *
   * `draft` grafiğin v1 İZDÜŞÜMÜ ve dallanmayı taşımıyor: `false` kolunun altı,
   * ikinci tetikleyici ve birincil yolun dışındaki her kutu orada YOK. Alan
   * eklenmeden önce ekran yanıttan yalnız `draft`ı alıp tuvali `graphFromLegacy`
   * ile yeniden kuruyordu; sonucu ölçüldü: modelin ürettiği dal ekrana HİÇ
   * ulaşmıyordu ve kullanıcının elle çizdiği dal her istemde siliniyordu —
   * yani Faz 7'nin satın aldığı tek şey görünmüyordu.
   *
   * Kısmi düzenlemede de bu alan taban: ekran onu `current` gövdesinin `graph`
   * alanında geri gönderiyor ve model kendi yamasını onun ÜSTÜNE yazıyor.
   */
  graph: WorkflowGraph;
  /**
   * Modelin yaptığı varsayımlar, kullanıcının okuyacağı dilde DEĞİL ham
   * cümleler hâlinde. "Eşik verilmediği için sektör ortalaması 50 ₺ alındı"
   * gibi. Boş olabilir.
   *
   * i18n anahtarı DEĞİL düz metin: modelin ne varsaydığı önceden bilinemez,
   * sabit bir anahtar listesine sığmaz. Kullanıcının istemi hangi dildeyse
   * yanıt da o dilde geliyor (sistem istemi bunu söylüyor).
   */
  notes: string[];
  /// `validate` ucunun bu taslak için verdiği sonuç — ekran ikinci bir tur
  /// atmasın diye burada. Uyarılar taslağı geçersiz kılmaz.
  validation: ValidationResponse;
}

// ═══════════════════════════════════════════════════════════════════════════
// Koşular, aksiyonlar, onay kuyruğu
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 IDEMPOTENCY ANAHTARI — DETERMİNİSTİK
 *
 *   sha256(`${workflowRunId}|${actionIndex}|${targetId}|${actionType}|${targetValue}`)
 *
 * `WorkflowActionLog.idempotencyKey` `@unique`. Retry ikinci bir bütçe artışı
 * YAPAMAZ; unique ihlali HATA DEĞİL, "zaten uygulandı" olarak ele alınır ve
 * NO-OP döner. Rastgele üretilen bir anahtar bu korumayı yok eder.
 */
export interface ActionLogRow {
  id: string;
  workflowRunId: string;
  workflowId: string;
  workflowName: string;
  actionType: string;
  targetLevel: MetricLevel;
  targetId: string;
  targetName: string | null;
  /// LG2 — kararın neye dayandığı satırda görünür.
  inputMetrics: Partial<Record<MetricKey, number | null>> | null;
  beforeState: Record<string, unknown> | null;
  afterState: Record<string, unknown> | null;
  status: ActionStatus;
  /// LG6 — platform hata mesajı HAM hâliyle (jeton alanları temizlenmiş).
  error: string | null;
  /// LG5 — idempotency anahtarı görünür; destek aynı işi iki kez aramasın.
  idempotencyKey: string;
  approvedById: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  appliedAt: string | null;
  createdAt: string;
  /// ON5 — süre aşımı anı. Şemada kolon YOK; `createdAt + APPROVAL_TTL_DAYS`
  /// olarak TÜRETİLİR.
  expiresAt: string | null;
  /// LG7 — geri alınabilir mi (beforeState var ve durum APPLIED).
  canRollback: boolean;
  /**
   * LG5 — kaç kez denendi. Bu yorum ESKİDEN "kolon YOK, alan hep null döner"
   * diyordu; kolon o zamandan beri şemaya girdi (`attempts Int @default(0)`)
   * ve `toActionLogRow` artık satırdan okuyor. Yorumun eskimesi alanın üç faz
   * boyunca null kalmasının sebebiydi — sözleşme yorumu gerçeği anlatmalı.
   *
   * Tip `| null` KALIYOR: bu ucun eski yanıtlarını okuyan istemciler ve
   * kolondan önce yazılmış satırlar için null hâlâ geçerli bir değer.
   */
  attempts: number | null;
}

export const actionLogQuerySchema = listQuerySchema.extend({
  workflowId: z.uuid().optional(),
  adAccountId: z.uuid().optional(),
  status: actionStatusSchema.optional(),
  actionType: z.enum(ACTION_TYPES).optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  sort: z.enum(['createdAt', 'appliedAt']).default('createdAt'),
  dir: sortDirSchema,
});

export interface WorkflowRunRow {
  id: string;
  workflowId: string;
  workflowName: string;
  status: RunStatus;
  /// AUT-06 — "neden tetiklendi"nin kanıtı. Bu alan boş bırakılırsa ON2 ve
  /// LG2 ekranları YAZILAMAZ.
  triggerSnapshot: {
    metric?: MetricKey;
    value?: number;
    threshold?: number;
    scopeSize?: number;
    reason?: string;
    /// Faz 7b — olay koşusu: hangi olay, hangi hedef (kapsam buna indi).
    event?: string;
    eventTargetId?: string | null;
    eventTargetLevel?: string | null;
    /// Yayıncının bağlamı — KİŞİSEL VERİ YOK (yorum metni/yazar taşınmıyor).
    eventContext?: Record<string, unknown>;
  } | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
  actionCounts: Record<ActionStatus, number>;
}

/**
 * ON5 — onaylanmayan öneri kaç gün sonra düşer. Doküman süre VERMİYOR.
 * 3 gün: hafta sonunu kapsar ama bir hafta önceki metriğe dayanan bir
 * aksiyonun bugün uygulanması yanlış olur.
 */
export const APPROVAL_TTL_DAYS = 3;

export const approvalDecisionSchema = z.object({
  /// Toplu onayda birden fazla.
  actionLogIds: z.array(z.uuid()).min(1).max(100),
  decision: z.enum(['approve', 'reject']),
  /// Reddetmede isteğe bağlı not; denetim kaydına yazılır.
  note: z.string().trim().max(300).optional(),
});
export type ApprovalDecisionInput = z.infer<typeof approvalDecisionSchema>;

/// ON4 — toplu onayda KISMİ BAŞARI normaldir; bazıları süresi geçmiş,
/// bazıları guardrail'e takılmış olabilir. Yanıt hepsini ayrı ayrı raporlar.
export interface ApprovalDecisionResponse {
  applied: string[];
  rejected: string[];
  failed: { id: string; code: string; message: string }[];
}

/// LG7 — geri alma. Orijinal satır ROLLED_BACK olur ve TELAFİ aksiyonu YENİ
/// bir log satırı olarak yazılır. Geçmiş ÜZERİNE YAZILMAZ — defter mantığının
/// aynısı; "ne olduğu" ile "ne düzeltildiği" ayrı iki gerçek.
export const rollbackSchema = z.object({
  reason: z.string().trim().min(3).max(300),
});
export type RollbackInput = z.infer<typeof rollbackSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Şablonlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ✅ Faz 3 — KATALOG ARTIK VERİ.
 *
 * Faz 2'ye kadar şablonlar `apps/api/src/automation/templates.catalog.ts`
 * içinde SABİT KODDU (6 adet) ve `/admin/templates` salt okunurdu. Gerekçe
 * şuydu: *"`WorkflowTemplate` tablosu YOK ve şema donmuş; düzenleme ekranı
 * hiçbir kodun okumadığı bir ayarı kaydeder — kullanıcıya yalan."*
 *
 * Faz 1 tabloyu açtı, Faz 3 doldurdu (40 şablon) ve `/admin/templates`
 * düzenlenebilir oldu.
 *
 * ---------------------------------------------------------------------------
 * Kategori dili DEĞİŞTİ
 * ---------------------------------------------------------------------------
 * Eski liste `protect | scale | report | creative` idi ve bizim uydurduğumuz
 * bir bölümlemeydi. Yenisi Wask'ın kategori dili — kullanıcının başka bir
 * üründe zaten öğrendiği sözcükler. `report` düştü çünkü rapor bir KATEGORİ
 * değil bir AKSİYON (`notification` altına girdi); `keyword`, `lead` ve `ai`
 * eklendi.
 *
 * 🔴 Kurulmuş kurallar kategoriye referans VERMİYOR (yalnız `id`ye), bu yüzden
 * değişim geriye dönük kırılma üretmiyor.
 */
export const TEMPLATE_CATEGORIES = [
  'performance',
  'notification',
  'budget',
  'keyword',
  'lead',
  'ai',
] as const;
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];
export const templateCategorySchema = z.enum(TEMPLATE_CATEGORIES);

export interface WorkflowTemplate {
  /// Okunur kimlik ("perf-pause-cpa-over-target") ve BİRİNCİL ANAHTAR.
  /// Kurulmuş kurallar buna referans veriyor.
  id: string;
  /// i18n anahtarları; şablon adı ve açıklaması çeviriden gelir.
  nameKey: string;
  descriptionKey: string;
  category: TemplateCategory;
  /// SB2 — okunur zincir özeti.
  summaryKeys: string[];
  /// Hangi platformlarda anlamlı. BOŞ DİZİ = hepsi. Anahtar kelime şablonları
  /// yalnız `['GOOGLE']` — Meta'da kelime kavramı yok.
  platforms: AdPlatform[];
  trigger: WorkflowTrigger;
  conditions: ConditionGroup;
  actions: WorkflowAction[];
  /// Önerilen mod; kullanıcı değiştirebilir.
  mode: AutomationMode;
  /// "18.805 kez kullanıldı" — sosyal kanıt. Kurulumda artıyor.
  installCount: number;
  /**
   * DALLANAN ŞABLONUN GÖVDESİ (Faz 7) — yalnız dallananlarda dolu.
   *
   * 🔴 Doğrusal şablonlarda YOK ve olmamalı: onların grafiği `graphFromLegacy`
   * ile v1 kolonlarından deterministik olarak üretiliyor, kolona yazmak aynı
   * şeyin ikinci bir kopyasını saklamak ve ikisinin ayrışabilmesi demekti
   * (gerekçenin uzunu `template-seed.ts` → `TemplateSeed.graph`).
   *
   * Dolu olduğunda kurulum onu kurala aynen taşıyor (`installTemplate`); v1
   * kolonları yalnız `true` kolunu anlatıyor, yani grafik olmadan kurulan bir
   * dallanan şablon kuralın YARISI oluyordu.
   *
   * ⚠️ `workflow_templates.graph` kolonu açılana kadar HER ZAMAN `undefined`
   * (bkz. `packages/db/seed/templates.ts` başlığı).
   */
  graph?: WorkflowGraph;
}

/**
 * Admin şablon düzenleme gövdesi (`PUT /admin/templates/:id`).
 *
 * 🔴 `id` DEĞİŞTİRİLEMEZ ve gövdede YOK: kurulmuş kurallar ona referans
 * veriyor ve kimliği değiştirmek onları kaynaksız bırakırdı.
 *
 * 🔴 `installCount` da YOK. O bir SAYAÇ, bir ayar değil; elle yazılabilir
 * olsaydı sosyal kanıt uydurulabilir bir sayıya dönerdi. (Tohum değeri seed'de
 * veriliyor — orası bir kez, burası her gün.)
 */
export const adminTemplateUpdateSchema = z.object({
  category: templateCategorySchema.optional(),
  platforms: z.array(z.enum(['META', 'GOOGLE', 'TIKTOK', 'OPENAI', 'LINKEDIN'])).max(5).optional(),
  trigger: workflowTriggerSchema.optional(),
  conditions: conditionGroupSchema.optional(),
  actions: workflowActionsSchema.optional(),
  mode: automationModeSchema.optional(),
  /**
   * Yayından kaldırmak SİLMEK DEĞİL. Kurulmuş kurallar `id`ye referans
   * veriyor; satırı silmek onların kaynağını koparır ve `/admin/templates`
   * "kaç kiracıda kurulu" sayısını da kaybederdi.
   */
  published: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type AdminTemplateUpdateInput = z.infer<typeof adminTemplateUpdateSchema>;

export const templateInstallSchema = z.object({
  templateId: z.string().min(1).max(60),
  scope: workflowScopeSchema,
  /// Şablondan kurulan kural HER ZAMAN `enabled: false` doğar ve kullanıcı
  /// editörde gözden geçirir (SB4). Tek tıkla para hareket ettiren bir kural
  /// açmak, kullanıcının okumadığı bir sözleşmeyi imzalatmaktır.
  name: z.string().trim().min(2).max(120).optional(),
});
export type TemplateInstallInput = z.infer<typeof templateInstallSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Guardrail sabitleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * KE13 — günlük aksiyon limiti sayacı.
 *
 * Doküman "hangi zaman diliminde, hangi durumdaki aksiyonlar" demiyor.
 * Sözleşme kararı: WORKSPACE ZAMAN DİLİMİNDE gün başına, YALNIZCA `APPLIED`
 * aksiyonlar sayılır. Onay bekleyen ya da atlanan aksiyon kotayı yakmaz —
 * yakarsa kullanıcı hiçbir şey olmadan limite takılır.
 */
export const ACTION_COUNTER_COUNTS_ONLY = 'APPLIED' as const;

export const AutomationErrorCode = {
  WORKFLOW_NOT_FOUND: 'WORKFLOW_NOT_FOUND',
  /// Kaydetme doğrulamasından geçemedi; `fieldErrors` dolu.
  WORKFLOW_INVALID: 'WORKFLOW_INVALID',
  /// Kuraldaki bütçe yüzdesi workspace tavanını aşıyor.
  GUARDRAIL_BUDGET_EXCEEDED: 'GUARDRAIL_BUDGET_EXCEEDED',
  /// Günlük aksiyon limiti doldu.
  GUARDRAIL_DAILY_LIMIT: 'GUARDRAIL_DAILY_LIMIT',
  /// Workspace SUGGEST kipinde; kural autopilot çalışamaz.
  AUTOPILOT_BLOCKED_BY_WORKSPACE: 'AUTOPILOT_BLOCKED_BY_WORKSPACE',
  /// Kapsamdaki hesaplardan biri kopuk — koşu atlandı (§9 güvenli duraklatma).
  CONNECTION_LOST: 'CONNECTION_LOST',
  /// Kapsam hiçbir varlığa çözülmedi.
  SCOPE_EMPTY: 'SCOPE_EMPTY',
  ACTION_NOT_FOUND: 'ACTION_NOT_FOUND',
  /// Aksiyonun sözleşmesi var ama uygulayıcısı henüz yazılmadı
  /// (`ACTION_TYPES_WITH_EXECUTOR` dışında). Kaydetmeyi ENGELLEMEZ; koşuda
  /// aksiyon `SKIPPED` olur ve gerekçe log satırında görünür.
  ACTION_NOT_IMPLEMENTED: 'ACTION_NOT_IMPLEMENTED',
  /// Onay süresi geçmiş.
  APPROVAL_EXPIRED: 'APPROVAL_EXPIRED',
  /// Aksiyon zaten karara bağlanmış.
  APPROVAL_ALREADY_DECIDED: 'APPROVAL_ALREADY_DECIDED',
  /// Geri alınamaz: önceki durum bilinmiyor ya da platformda değer sapmış.
  ROLLBACK_NOT_POSSIBLE: 'ROLLBACK_NOT_POSSIBLE',
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  /// Aynı kural zaten çalışıyor (kural başına kilit).
  RUN_IN_PROGRESS: 'RUN_IN_PROGRESS',
  /**
   * Faz 5 — kelime otomatik teklifte (`cpcBid = null`), teklif yazılamaz.
   *
   * Motorun atlama gerekçesiyle (`automation.engine.ts` → `BID_AUTOMATIC`)
   * AYNI dize: elle çağrı ile zamanlanmış koşu aynı duruma aynı adı vermeli,
   * yoksa kullanıcı iki ekranda iki farklı sebep okur ve aynı şey olduğunu
   * anlayamaz.
   *
   * ⚠️ Bu bir HATA, uyarı değil: `null`ı 0 sayıp yüzde uygulamak teklifi
   * kalıcı olarak sıfıra çakar ve kelime hiç gösterim almaz.
   */
  KEYWORD_BID_AUTOMATIC: 'KEYWORD_BID_AUTOMATIC',
  /**
   * `POST /automations/compose` — model bir taslak üretemedi.
   *
   * "Bir şeyler ters gitti" demiyor, AYRI bir kod: sebebi çoğu zaman istemin
   * kendisi ("reklamlarımı iyileştir" bir kural tarifi değil) ve arayüz bunu
   * bir arıza gibi değil bir soru gibi göstermeli — "biraz daha açık yazın".
   */
  COMPOSE_FAILED: 'COMPOSE_FAILED',

  // ── Faz 5 — kimlik kasası ─────────────────────────────────────────────────
  /// `credentialId` bu çalışma alanında yok (ya da silinmiş).
  CREDENTIAL_NOT_FOUND: 'CREDENTIAL_NOT_FOUND',
  /// Aynı adla ikinci bir kimlik bilgisi (`@@unique([workspaceId, name])`).
  CREDENTIAL_NAME_TAKEN: 'CREDENTIAL_NAME_TAKEN',
  /**
   * Seçilen başlık `WORKFLOW_CREDENTIAL_FORBIDDEN_HEADERS` içinde.
   *
   * `HTTP_CREDENTIAL_HEADER_FORBIDDEN` (grafik şeması) ile AYNI yasağın
   * ikinci kapısı ama AYRI kod: orada düğümün kendi `headers` sözlüğü
   * reddediliyor, burada kasaya YAZILAN satır. Tek kod olsaydı kullanıcı
   * hatayı akış editöründe arar, oysa sorun kimlik bilgisi formunda olurdu.
   */
  CREDENTIAL_HEADER_FORBIDDEN: 'CREDENTIAL_HEADER_FORBIDDEN',
  /**
   * Silinmek istenen kimlik bilgisine bir akış grafiği hâlâ atıf yapıyor.
   *
   * 🔴 Silmeyi ENGELLİYOR, sessizce izin verip akışı bozmuyor: `credentialId`
   * çözülemediğinde `action.http` düğümü çalışma anında düşer ve kullanıcı
   * bunu ancak koşu kaydında görür — kuralın kurulduğu gün değil, bozulduğu
   * gün de değil, kuralın çalıştığı gün.
   */
  CREDENTIAL_IN_USE: 'CREDENTIAL_IN_USE',

  // ── Faz 5 — gelen webhook ────────────────────────────────────────────────
  /// Token hiçbir kurala çözülmedi. 404: adresin GEÇERSİZ olduğunu söylüyor,
  /// "hangi kural" bilgisini sızdırmıyor.
  WEBHOOK_NOT_FOUND: 'WEBHOOK_NOT_FOUND',
  /// HMAC imzası yok ya da tutmuyor.
  WEBHOOK_SIGNATURE_INVALID: 'WEBHOOK_SIGNATURE_INVALID',
  /**
   * İmza doğrulanacaktı ama ham gövde okunamadı.
   *
   * `WEBHOOK_SIGNATURE_INVALID`ten AYRI olmak zorunda: bu bir SUNUCU
   * yapılandırma eksiği (`main.ts` → `WEBHOOK_PATHS`), çağıranın hatası
   * değil. Aynı kodu döndürmek, çağıranın kendi imzasını haftalarca
   * boşuna aramasına yol açardı.
   */
  WEBHOOK_RAW_BODY_MISSING: 'WEBHOOK_RAW_BODY_MISSING',
  /// Kural kapalı (ya da ajanı kapalı). Adres geçerli ama koşu yapılmadı.
  WEBHOOK_NOT_ENABLED: 'WEBHOOK_NOT_ENABLED',
  /// `cooldownMinutes` penceresi içinde ikinci çağrı — koşu yapılmadı.
  WEBHOOK_COOLDOWN: 'WEBHOOK_COOLDOWN',
} as const;
export type AutomationErrorCode = (typeof AutomationErrorCode)[keyof typeof AutomationErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// FAZ 5 — KİMLİK KASASI (`WorkflowCredential`)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 KİMLİK BİLGİSİ TAŞIYAMAYACAK BAŞLIKLAR — TEK LİSTE.
 *
 * `action.http` düğümünün kendi `headers` sözlüğü ve kasaya yazılan
 * `WorkflowCredential.header` AYNI yasağa tabi ve yasak İKİ yerde
 * uygulanıyor (şema kapısı + servis kapısı). İki liste olsaydı biri
 * güncellenmeyi kaçırır ve iki kapıdan biri açık kalırdı.
 *
 * Neden bu üçü: `Authorization` ve `Proxy-Authorization` standart kimlik
 * başlıkları, `Cookie` ise oturum taşıyıcısı. Üçü de grafik BELGESİNE
 * yazılabilseydi sır dışa aktarımda, şablonda ve `compose` isteminde model
 * bağlamına düz metin olarak girerdi. Kasadaki satır için de yasaklılar ve
 * gerekçe farklı: `Authorization: Bearer …` biçiminin şemasını biz
 * kurmuyoruz (`Basic` mi `Bearer` mi, ön ek dahil mi) — kullanıcının yazdığı
 * ham değeri o başlığa koymak, çalışmayan bir isteğin sebebini görünmez
 * kılardı. Özel başlık (`X-Api-Key`) belirsizlik taşımıyor.
 *
 * ⚠️ `automation-graph.ts` içindeki `FORBIDDEN_HTTP_HEADERS` bugün bu listenin
 * İKİZİ (aynı üç değer, ayrı yazılmış). O dosya Faz 3'ün; birleştirme isteği
 * raporda.
 */
export const WORKFLOW_CREDENTIAL_FORBIDDEN_HEADERS: readonly string[] = [
  'authorization',
  'cookie',
  'proxy-authorization',
];

/// Başlık adı — HTTP token grameri değil, onun DAR bir alt kümesi. Nokta,
/// alt çizgi ve boşluk dışarıda: `httpActionSchema.headers` anahtarlarıyla
/// AYNI desen olsun ki kasadan gelen başlık düğümünkiyle aynı kapıdan geçsin.
const credentialHeaderSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9-]{1,64}$/, 'Başlık adı geçersiz')
  .refine((h) => !WORKFLOW_CREDENTIAL_FORBIDDEN_HEADERS.includes(h.toLowerCase()), {
    message: 'CREDENTIAL_HEADER_FORBIDDEN',
  });

/**
 * Kimlik bilgisi oluşturma/güncelleme gövdesi.
 *
 * 🔴 `value` YALNIZ BU YÖNDE akıyor. Hiçbir okuma ucu onu döndürmüyor
 * (`WorkflowCredentialRow`da yok) — `TokenVaultService` ile şifrelenip
 * `credentialEnc` kolonuna yazılıyor ve tekrar yalnız giden isteğin başlığına
 * çıkıyor. "Düzenlemek için mevcut değeri göster" akışı BİLEREK yok: bir sır
 * ekranda görünebiliyorsa ekran görüntüsünde, tarayıcı geçmişinde ve destek
 * kaydında da görünebilir.
 */
export const workflowCredentialUpsertSchema = z.object({
  name: z.string().trim().min(1).max(60),
  header: credentialHeaderSchema,
  /// Ham sır. 4000 karakter, imzalı JWT'ler ve uzun API anahtarları sığsın.
  value: z.string().min(1).max(4000),
});
export type WorkflowCredentialUpsertInput = z.infer<typeof workflowCredentialUpsertSchema>;

/// Güncellemede `value` isteğe bağlı: adı değiştirmek için sırrı yeniden
/// yazdırmak, kullanıcıyı sırrı bir yerden kopyalamaya zorlar.
export const workflowCredentialPatchSchema = workflowCredentialUpsertSchema.partial().refine(
  (patch) => Object.keys(patch).length > 0,
  { message: 'EMPTY_PATCH' },
);
export type WorkflowCredentialPatchInput = z.infer<typeof workflowCredentialPatchSchema>;

/**
 * Listede dönen satır — SIRRIN KENDİSİ YOK.
 *
 * `valueHint` sırrın son dört karakteri: kullanıcı iki anahtarı ayırt
 * edebilsin diye. Tamamı gösterilmiyor, ilk karakterler de gösterilmiyor —
 * çoğu sağlayıcı anahtarının ÖN EKİ sabittir (`sk_live_`, `AIza`) ve baştan
 * göstermek entropi vermez, kuyruktan göstermek verir.
 */
export interface WorkflowCredentialRow {
  id: string;
  name: string;
  header: string;
  valueHint: string;
  createdAt: string;
  updatedAt: string;
  /// Bu kimlik bilgisine atıf yapan kural sayısı. 0'dan büyükse silme reddedilir.
  usedByWorkflows: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// FAZ 5 — GİDEN `action.http` SONUCU
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Yanıt gövdesi tavanı — 64 KB.
 *
 * 🔴 STREAM ÜZERİNDE uygulanıyor, `await response.text()` ile DEĞİL: gövdenin
 * tamamını belleğe alıp sonra kırpmak, 2 GB'lık bir yanıtın süreci
 * öldürebilmesi demek. Kullanıcının dış ucundan gelen boyut bizim
 * kontrolümüzde değil ve saldırgan onu kasten büyütebilir.
 *
 * 64 KB neden yeterli: bu gövde bir KANIT, bir veri kaynağı değil. İfade dili
 * olmadığı için (`GraphEdgeValue` başlığı) hiçbir düğüm yanıtın içinden bir
 * alan okuyamıyor; gövde yalnız koşu kaydında "uç ne dedi" sorusunu
 * cevaplıyor.
 */
export const HTTP_ACTION_MAX_RESPONSE_BYTES = 64 * 1024;

/**
 * 🔴 `WorkflowActionLog.actionType` KOLONUNA YAZILAN DEĞER — TEK YERDE.
 *
 * `action.http` bir `ActionType` DEĞİL (`ACTION_TYPES` içinde yok ve olmamalı;
 * gerekçe `GRAPH_HTTP_ACTION_KIND` başlığında) ama log satırı yine de bir
 * `actionType` taşımak zorunda — kolon `String` ve boş bırakılamaz.
 *
 * Bu sabit, satırı YAZAN (grafik motoru) ile satırı UYGULAYAN
 * (`ActionExecutorService.perform`) tarafın aynı dizeyi okumasını sağlıyor.
 * İki tarafın `'http'` dizesini ayrı ayrı yazması, bir gün birinin
 * `'http_call'` demesi ve uygulayıcının o satıra "bilinmeyen aksiyon tipi"
 * demesi demekti — yani sessizce hiç yapılmayan bir dış çağrı.
 */
export const HTTP_ACTION_LOG_TYPE = 'http';

/// `WorkflowActionLog.afterState`e yazılan gövde özeti daha da dar: log satırı
/// ekranda açılıyor ve 64 KB'lık bir metin o ekranı kullanılamaz kılar.
export const HTTP_ACTION_LOG_BODY_CHARS = 2000;

/**
 * Giden çağrının SONUCU — `afterState.outcome` alanına yazılan kapalı liste.
 *
 * `ok` bir boolean olarak yeterli değil: "neden olmadı" sorusunun cevabı
 * kullanıcı için farklı EYLEMLER anlamına geliyor. `BLOCKED_HOST` kuralı
 * düzeltmeyi gerektiriyor, `TIMEOUT` karşı tarafı, `STATUS_ERROR` ise
 * çoğu zaman gövdeyi. Tek bir "başarısız" hepsini aynı çaresizliğe
 * indirgerdi.
 */
export const HttpActionOutcome = {
  /// 2xx alındı.
  OK: 'OK',
  /// Yanıt geldi ama 2xx değil. Yönlendirmeler (3xx) de BURAYA düşüyor —
  /// takip KAPALI, bkz. `HTTP_ACTION_FOLLOW_REDIRECTS`.
  STATUS_ERROR: 'HTTP_STATUS_ERROR',
  /// `timeoutMs` doldu; istek iptal edildi.
  TIMEOUT: 'HTTP_TIMEOUT',
  /// Ana ad çözülemedi.
  DNS_FAILED: 'HTTP_DNS_FAILED',
  /// Ana ad ya da ÇÖZÜLEN ADRES özel/yerel ağa işaret ediyor (SSRF kapısı).
  BLOCKED_HOST: 'HTTP_URL_BLOCKED_HOST',
  /// URL ayrıştırılamadı ya da `https` değil.
  URL_INVALID: 'HTTP_URL_INVALID',
  /// Başlıklardan biri kimlik başlığı (ikinci kapı; şema da reddediyor).
  HEADER_FORBIDDEN: 'HTTP_CREDENTIAL_HEADER_FORBIDDEN',
  /// `credentialId` çözülemedi — kimlik bilgisi silinmiş.
  CREDENTIAL_MISSING: 'HTTP_CREDENTIAL_MISSING',
  /// TCP/TLS kurulamadı ya da bağlantı koptu.
  CONNECTION_FAILED: 'HTTP_CONNECTION_FAILED',
} as const;
export type HttpActionOutcome = (typeof HttpActionOutcome)[keyof typeof HttpActionOutcome];

/**
 * 🔴 YÖNLENDİRME TAKİBİ KAPALI ve sabit `false`.
 *
 * Bir yönlendirme, SSRF kontrolünün YENİDEN yapılması gereken yeni bir adres
 * demek: `https://acik.example.com` → `302 Location: http://169.254.169.254/`
 * zinciri, kapıdan geçmiş bir istekle metadata ucuna varır. Takip açılacaksa
 * her adımda ana ad süzgeci + DNS çözümü + adres süzgeci yeniden
 * uygulanmalı ve adım sayısı bağlanmalı.
 *
 * Kapalı tutmanın bedeli küçük: yönlendirme yapan bir uç 3xx olarak
 * `STATUS_ERROR` üretiyor ve kullanıcı NİHAİ adresi kuralına yazıyor.
 * Bedelin ölçülebilir, alternatifin ölçülemez olması bu kararın tamamı.
 */
export const HTTP_ACTION_FOLLOW_REDIRECTS = false;

/** Giden çağrının koşu kaydına düşen izi. */
export interface HttpActionResult {
  outcome: HttpActionOutcome;
  /// Yanıt alınamadıysa `null`.
  status: number | null;
  durationMs: number;
  /// Kırpılmış gövde (`HTTP_ACTION_LOG_BODY_CHARS`).
  bodyPreview: string;
  /// Gövde tavanı aşıldığı için kesildi mi.
  truncated: boolean;
  /**
   * Bağlanılan IP.
   *
   * Kayda giriyor çünkü SSRF savunmasının KANITI bu: "hangi adrese gittik"
   * sorusunun cevabı olmadan bir rebinding şüphesi soruşturulamaz.
   */
  resolvedAddress: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════
// FAZ 5 — GELEN WEBHOOK
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Webhook token'ının BAYT uzunluğu (hex'e çevrilince 64 karakter).
 *
 * Token'ın kendisi sır: uç kimlik doğrulaması YAPMIYOR ve yapamaz (çağıran
 * üçüncü bir sistem, bizim oturumumuz yok). 32 bayt, tahmin etmeyi
 * kaba kuvvetle imkânsız kılıyor; daha kısası bir URL'in "biraz gizli"
 * olmasına güvenmek olurdu ve bu adres para hareket ettirebiliyor.
 */
export const WEBHOOK_TOKEN_BYTES = 32;

/// İmza başlığı. `X-` ön eki bilinçli: standart bir başlık değil ve bir gün
/// standartlaşırsa çakışmasın.
export const WEBHOOK_SIGNATURE_HEADER = 'x-mentorivo-signature';

/**
 * Gövde tavanı — 64 KB.
 *
 * Gövde KANIT olarak saklanıyor (`triggerSnapshot`), koşula girmiyor. Tavansız
 * bırakmak, kimlik doğrulaması olmayan bir uca sınırsız veri yazdırmak demekti.
 */
export const WEBHOOK_MAX_BODY_BYTES = 64 * 1024;

/// `triggerSnapshot`a yazılan gövde daha dar tutuluyor: koşu detay ekranı
/// bunu açıyor ve 64 KB'lık bir JSON o ekranı okunamaz kılar.
export const WEBHOOK_SNAPSHOT_BODY_CHARS = 4000;

/**
 * Uç YANITI — çağıran dış sistem bunu görüyor.
 *
 * 🔴 `ran: false` da 200 dönüyor (susturma penceresi, kapalı kural). Sebep:
 * dış sistem 4xx/5xx'i BAŞARISIZLIK sayıp yeniden dener ve o yeniden
 * denemeler susturmayı da, kapalı kuralı da değiştirmez — yalnız gürültü
 * üretir. İsteğin ALINDIĞI doğru; koşunun yapılmadığı `reason`da yazılı.
 *
 * ⚠️ İmza hatası ve bilinmeyen token BUNUN DIŞINDA: onlar gerçekten
 * reddedilen isteklerdir ve 401/404 dönüyorlar.
 */
export interface WorkflowWebhookResponse {
  received: true;
  ran: boolean;
  /// `AutomationErrorCode` değeri ya da `null` (koşu yapıldı).
  reason: string | null;
  runId: string | null;
}

/**
 * Kural sahibinin gördüğü webhook bilgisi.
 *
 * `secret` her okumada TEKRAR gösterilebiliyor ve bu bilinçli: türetilmiş bir
 * değer (bkz. `WebhookService.deriveSecret`), saklanmıyor, dolayısıyla
 * "bir kez göster sonra unut" akışı sahte bir güvenlik olurdu — sunucu onu
 * her an yeniden üretebiliyor.
 */
export interface WorkflowWebhookInfo {
  /// Tam adres. `null` = kuralın henüz bir webhook token'ı yok.
  url: string | null;
  token: string | null;
  /// HMAC-SHA256 anahtarı (hex). İmza kapalıysa da dönüyor — kullanıcı
  /// imzayı açtığında ikinci bir ekrana gitmesin.
  secret: string | null;
  signatureHeader: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Koşu / onay listeleri ve aç-kapa gövdesi — 18 Eyl 2026'da API'den taşındı
// ═══════════════════════════════════════════════════════════════════════════
//
// Eski yerleri `apps/api/src/automation/automation.queries.ts`; oradaki adlar
// (`runListQuerySchema`, `approvalListQuerySchema`, `setEnabledSchema`) bu
// tanımlara işaret ediyor. Taşınma sebebi: geliştirici API'sinin sözleşme
// kaydı (`developer-api.ts`) ve MCP sunucusu aynı şemayı okuyor.

export const automationRunListQuerySchema = listQuerySchema.extend({
  workflowId: z.uuid().optional(),
  status: runStatusSchema.optional(),
  from: z.iso.datetime().optional(),
  to: z.iso.datetime().optional(),
  dir: sortDirSchema,
});
export type AutomationRunListQuery = z.infer<typeof automationRunListQuerySchema>;

export const automationApprovalListQuerySchema = listQuerySchema.extend({
  workflowId: z.uuid().optional(),
  /// Süresi geçmiş onaylar varsayılan olarak GİZLİ: kullanıcı üç gün önceki
  /// bir metriğe dayanan aksiyonu onaylayabilseydi, onayladığı şey artık
  /// doğru olmayabilirdi.
  includeExpired: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .default(false),
});
export type AutomationApprovalListQuery = z.infer<typeof automationApprovalListQuerySchema>;

/// OT2 — satır içi aç/kapa. `confirmAutopilot` KE17'nin ikinci onayı.
export const automationSetEnabledSchema = z.object({
  enabled: z.boolean(),
  confirmAutopilot: z.boolean().default(false),
});
export type AutomationSetEnabledInput = z.infer<typeof automationSetEnabledSchema>;
